/**
 * Audio Playback Manager
 * 
 * Handles audio playback for incoming AI voice responses.
 * Supports both PCM16 data (Unity-style) and MediaStream (WebRTC).
 * 
 * Features:
 * - PCM16 data playback with proper AEC
 * - MediaStream playback with visualization
 * - Multiple concurrent streams
 * - Playback monitoring and state management
 * - Volume control per stream
 * - Object pooling for zero-allocation runtime (game dev technique)
 */

import { AudioConfig } from './types';
import { AudioNodePool, PooledAudioNodes } from './AudioNodePool';
import { logger } from '../../utils/logger';
import { recordAudioHealth, getAudioTelemetryContext } from './AudioTelemetry';

export interface PlaybackEvents {
  onPlaybackStart: () => void;
  onPlaybackStop: () => void;
  onError: (error: Error) => void;
}

export class AudioPlayback {
  private audioContext: AudioContext;
  private config: AudioConfig;
  private events: Partial<PlaybackEvents> = {};
  
  // 🎮 Object Pool: Zero-allocation runtime (game dev technique)
  private nodePool: AudioNodePool;
  private activePooledNodes: Map<AudioBufferSourceNode, PooledAudioNodes> = new Map();
  
  // Track playback state
  private isPlaying = false;
  private isPaused = false;
  
  // Streaming buffer system (for WebSocket chunks)
  private streamingBuffer: Float32Array[] = []; // Ring buffer of float32 chunks
  private prebufferTime: number; // Dynamic based on device
  private prebufferSamples = 0;
  private totalBufferedSamples = 0;
  private playbackStarted = false;
  private scheduledNodes: AudioBufferSourceNode[] = [];
  private nextScheduledTime = 0;
  private minScheduleAhead: number; // Dynamic based on device
  private isMobile: boolean;
  
  // 📱 MOBILE: ScriptProcessorNode for playback (fallback from AudioBufferSourceNode)
  private playbackProcessor: ScriptProcessorNode | null = null;
  private playbackRingBuffer: Float32Array[] = []; // Circular buffer for ScriptProcessor
  private playbackBufferReadIndex = 0; // Track position in CURRENT chunk
  // Index into playbackRingBuffer of the chunk currently being drained. We advance this
  // instead of shift()-ing so the audio callback stays O(1); lazy compaction below.
  private chunkReadIndex = 0;
  private readonly CHUNK_COMPACT_THRESHOLD = 32; // Compact when this many chunks are behind the cursor
  // Preallocated scratch buffer for the audio callback: avoids allocating a JS array every
  // ~85ms which, under pressure, is a real source of jitter in Firefox. Sized for the worst
  // case at 48kHz output / 24kHz source ratio (2048 source samples per 4096-sample callback)
  // with safety margin.
  private sourceSamplesBuffer: Float32Array = new Float32Array(4096);
  private silenceCounter = 0; // Track consecutive silence callbacks for auto-stop
  
  // 🎯 Adaptive buffering system
  // Per-browser limits — Firefox Mobile gets a tight cap because larger buffers
  // desync the browser's AEC: setPlaybackActive(true/false) fires when chunks
  // enter/leave the JS pipeline, but with 2-3s of buffer the speaker is still
  // playing audio that left the JS pipeline seconds ago. The mic captures that
  // tail and the VAD has no playbackActive guard. See `getMinBufferMs` /
  // `getMaxBufferMs` for the per-browser values.
  private readonly BUFFER_ADJUST_STEP = 100; // Adjust by 100ms at a time
  private getMinBufferMs(): number {
    // Floor for adaptive shrinking. We want to be able to go quite low when the
    // network is healthy. Mobile devices benefit most because their AEC is
    // weaker and large buffers cause the avatar's voice to leak into the mic.
    if (this.isFirefoxMobile) return 400;
    if (this.isMobile) return 600;
    return 800;
  }
  private getMaxBufferMs(): number {
    // Ceiling for adaptive growth. Firefox Mobile cap is tightest because its AEC
    // decalibrates above ~600-800ms of speaker delay. Other mobiles get a larger
    // cap (1500ms) — still well below the previous 3000ms but with more headroom
    // for jitter, since they show fewer avatar-loop symptoms in practice.
    if (this.isFirefoxMobile) return 1000;
    if (this.isMobile) return 1500;
    return 3000;
  }
  private bufferHealthMonitor: NodeJS.Timeout | null = null;
  private minBufferLevelSeen = 1.0; // Track lowest buffer % seen (1.0 = 100%)
  private bufferStarvationCount = 0; // How many times we ran dangerously low
  private timeInHealthyZone = 0; // Time spent with healthy buffer (ms)
  private lastBufferCheckTime = 0;

  // 🎯 Network jitter detection for adaptive prebuffering
  private chunkArrivalTimes: number[] = []; // Track last 10 chunk arrivals
  private readonly MAX_LATENCY_SAMPLES = 10;
  private averageInterChunkLatency = 0; // Rolling average of time between chunks
  private jitterDetected = false;

  // 🎯 Predictive prebuffering
  // learnedPrebufferMs carries between responses — if the first response on a slow network
  // needed a larger buffer, subsequent responses start from that value instead of the
  // per-browser default (avoids relearning on every response).
  private learnedPrebufferMs = 0; // 0 = not yet learned; set lazily on first use
  private earlyResponseGaps: number[] = []; // inter-arrival gaps for first EARLY_SAMPLE_COUNT chunks
  private readonly EARLY_SAMPLE_COUNT = 5;
  private earlyEvalDone = false; // true once evaluateNetworkHealth has fired for this response
  // Hard cap (ms) for the learned/current prebuffer, so a single bad blip doesn't permanently
  // leave us with a long latency window. Kept below the device-specific MAX to leave headroom
  // for reactive growth from the buffer health monitor.
  private getPredictiveBufferCapMs(): number {
    if (this.isFirefoxMobile) return 800;
    if (this.isMobile) return 1200;
    return 2500;
  }

  // 🦊 Firefox-specific fixes
  private isFirefoxMobile = false;
  private isFirefox = false; // 🦊 Desktop Firefox also needs special handling
  
  // 🦊 FIX: Use ScriptProcessorNode for Firefox (avoids AudioBufferSourceNode scheduling issues)
  private useScriptProcessor = false;
  
  // Web Audio API nodes for incoming audio (AI voice)
  private playbackSources: Map<string, MediaStreamAudioSourceNode> = new Map();
  private playbackProcessors: Map<string, ScriptProcessorNode> = new Map();
  private playbackGains: Map<string, GainNode> = new Map();
  private playbackAnalysers: Map<string, AnalyserNode> = new Map();
  private playbackAnalyserNode: AnalyserNode | null = null; // Main analyser for visualization
  
  // ⏸️ Global master gain for pause/resume control
  private masterGainNode: GainNode | null = null;
  private savedVolume: number = 1.0; // Store volume when pausing
  
  // Dynamics compressor for loudness normalization across OpenAI voices
  private compressorNode: DynamicsCompressorNode | null = null;

  // Per-utterance peak normalization (AGC). OpenAI TTS sometimes returns a quiet
  // response and the user can't raise system volume past 100%. We measure the
  // peak across the opening window of each response and ramp this gain node up
  // (never down — clamped at 1.0) to bring it to a target headroom level.
  private normalizationGainNode: GainNode | null = null;
  private normalizationPeak: number = 0;
  private normalizationSamplesObserved: number = 0;
  private normalizationDecided: boolean = false;
  private static readonly NORMALIZATION_WINDOW_SAMPLES = 12000; // 500ms @ 24kHz
  private static readonly NORMALIZATION_TARGET_PEAK = 0.89;     // ~-1 dBFS
  private static readonly NORMALIZATION_MIN_GAIN = 1.0;         // never attenuate
  private static readonly NORMALIZATION_MAX_GAIN = 4.0;         // ~+12 dB cap
  private static readonly NORMALIZATION_SILENCE_PEAK = 0.05;    // below = silence, skip
  
  // ⏸️ Pause state: Store chunks that haven't been scheduled yet
  private pausedChunks: Float32Array[] = []; // Desktop: Chunks waiting to be scheduled
  private pausedRingBuffer: Float32Array[] = []; // Mobile: Chunks in ring buffer
  private pausedBufferReadIndex: number = 0; // Mobile: Current read position
  private pausedTotalBufferedSamples: number = 0; // Total samples in paused buffers
  private pausedNextScheduledTime: number = 0; // Desktop: When next chunk should play
  
  // HTML audio elements for Chrome autoplay fallback
  private playbackAudioElements: Map<string, HTMLAudioElement> = new Map();
  
  // Playback monitoring
  private playbackMonitorInterval: NodeJS.Timeout | null = null;
  
  // Debug flags
  private playStartLogged = false;
  private sampleRateLogged = false;
  private conversionLogged = false;
  
  // Response tracking (for logging)
  private responseCount = 0;
  
  // Track if response.audio.done was received (to handle late-arriving chunks)
  // This allows chunks that arrive AFTER response.audio.done to still be scheduled
  private responseFinished = false;
  // 🎯 Text-audio sync: Monotonic counter incremented on each new response.
  // Scheduled nodes capture the current generation; when onended fires, if the
  // generation has changed, a new response has started and we must NOT fire
  // onPlaybackStop (the stop belongs to the previous response). This prevents
  // the "sem itálico" bug where responseFinished=true from the previous response
  // leaks into the new response, causing premature onPlaybackStop on gaps.
  private responseGeneration: number = 0;

  // 🎯 Text-audio sync: accumulated playback time (ms) — only advances while audio
  // is actively being scheduled/drained. Naturally freezes during pause and buffer
  // underruns. Used by useSyncedTextReveal to throttle text reveal proportionally.
  // NOTE: This is the REAL-TIME accumulated played time (ScriptProcessor path drains
  // samples in the audio callback, so its increment is already real-time). The
  // AudioBufferSourceNode path does NOT use this for elapsed — see below.
  private accumulatedPlayedMs: number = 0;
  // 🎯 Text-audio sync: total audio samples received for this response (for duration estimate)
  private totalSamplesReceived: number = 0;
  // 🎯 Text-audio sync (AudioBufferSourceNode path): the AudioContext.currentTime at
  // which playback actually started for this response. Used to compute REAL elapsed
  // playback time (audioContext.currentTime - playbackStartContextTime) instead of
  // accumulating scheduled chunk durations — which would race ahead of real playback
  // on fast networks (chunks scheduled ahead via minScheduleAhead) and cause the
  // karaoke text to reveal all at once while audio is still playing.
  private playbackStartContextTime: number = 0;
  // 🎯 Text-audio sync (AudioBufferSourceNode path): accumulated REAL played time at
  // the moment playback was paused or stopped. Lets us freeze the elapsed value when
  // not actively playing (pause / between responses) and resume correctly.
  private accumulatedRealPlayedMs: number = 0;

  // 🎯 Per-response health stats (for diagnosing slow-network / Firefox issues)
  private responseStats = {
    startedAt: 0,
    startPrebufferMs: 0,
    minBufferLevelSeen: 1.0,
    starvationCount: 0,
    jitterEverDetected: false,
    lateChunksAfterDone: 0,
    totalChunks: 0,
    // audioBufferSource scheduling underruns: nextScheduledTime fell behind audioContext.currentTime
    // before a new chunk could be started. Each event corresponds to an audible silent gap.
    scheduleUnderrunCount: 0,
    scheduleUnderrunMaxMs: 0,
    scheduleUnderrunTotalMs: 0,
    // Chunks shorter than 200ms — proxy/MTU fragmentation signal
    smallChunkCount: 0,
  };

  // 🎯 Per-response callback-timing stats (for diagnosing GC / node-aging hypotheses).
  // Sampled every onaudioprocess invocation. Reservoir is a fixed-size ring used to
  // approximate p95 without allocating per callback — important on Firefox mobile where
  // GC pressure inside the audio thread is the leading suspect for the 3rd-turn collapse.
  private callbackCount = 0;
  private callbackSumMs = 0;
  private callbackMaxMs = 0;
  private readonly CALLBACK_RESERVOIR_SIZE = 64;
  private callbackReservoir = new Float32Array(this.CALLBACK_RESERVOIR_SIZE);
  private callbackReservoirCount = 0;
  private callbackReservoirIndex = 0;

  // Inter-callback gap reservoir. Same allocation discipline as callback duration —
  // measures browser scheduling jitter of onaudioprocess (gap between consecutive
  // invocations), separate from how long each invocation takes.
  private lastCallbackAt = 0; // performance.now() of previous onaudioprocess; 0 = none yet
  private interCallbackGapMaxMs = 0;
  private interCallbackGapSumMs = 0;
  private interCallbackGapCount = 0;
  private interCallbackGapReservoir = new Float32Array(this.CALLBACK_RESERVOIR_SIZE);
  private interCallbackGapReservoirCount = 0;
  private interCallbackGapReservoirIndex = 0;

  // Per-response chunk-size + inter-chunk-arrival stats (independent of jitter detector,
  // which has its own decay logic; this one is just plain min/avg/max + p95 reservoir).
  private chunkAudioSamplesTotal = 0;
  private chunkSizeMinSamples = Number.POSITIVE_INFINITY;
  private chunkSizeMaxSamples = 0;
  private chunkSizeSumSamples = 0;
  private chunkSizeCount = 0;
  private interChunkGapSumMs = 0;
  private interChunkGapMaxMs = 0;
  private interChunkGapCount = 0;
  private interChunkGapReservoir = new Float32Array(this.CALLBACK_RESERVOIR_SIZE);
  private interChunkGapReservoirCount = 0;
  private interChunkGapReservoirIndex = 0;
  private lastChunkArrivalAt = 0; // performance.now() of last chunk for THIS response

  // Rolling window of recent finished responses, for the on-screen NetworkQualityPill.
  // Per-response snapshots (not raw transient state) — the pill needs sustained signal,
  // and the transient `prebufferTimeMs` / `jitterDetected` flicker too much in conversational
  // turns to drive a stable indicator. Keeps the last 5; entries older than 60s are pruned
  // on read so the pill auto-clears when the network recovers.
  private recentResponseHealth: Array<{
    finishedAt: number; // performance.now() at end of response
    starvationCount: number;
    scheduleUnderrunCount: number;
    jitterEverDetected: boolean;
    endPrebufferMs: number;
    interChunkGapAvgMs: number;
  }> = [];

  // 🎯 Telemetry context (sessionId / userId) is read from a module-level singleton at
  // snapshot time — the host app sets it once via setAudioTelemetryContext.

  constructor(audioContext: AudioContext, config: AudioConfig) {
    this.audioContext = audioContext;
    this.config = config;
    
    // 🎮 Initialize object pool FIRST (before any audio operations)
    this.nodePool = new AudioNodePool(audioContext);
    this.nodePool.warmup(); // Pre-allocate all objects NOW (zero runtime allocation)
    
    // 🔧 BROWSER DETECTION: Different strategies for different browsers
    // Firefox has specific issues with Web Audio API, especially on mobile
    const isFirefox = navigator.userAgent.includes('Firefox');
    const isFirefoxMobile = isFirefox && /Android|iPhone|iPad|iPod/.test(navigator.userAgent);
    this.isFirefoxMobile = isFirefoxMobile;
    this.isFirefox = isFirefox; // 🦊 FIX #3: Track Firefox desktop too

    // 🔧 MOBILE DETECTION: Use more aggressive buffering on mobile devices
    // Mobile browsers have less consistent timing and more CPU interruptions
    this.isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    // 🦊 FIX: Firefox Desktop uses ScriptProcessorNode to avoid AudioBufferSourceNode issues
    // After ~5 minutes of conversation, Firefox starts dropping audio chunks when using
    // AudioBufferSourceNode scheduling. ScriptProcessorNode uses continuous callback model
    // which avoids this issue entirely.
    this.useScriptProcessor = this.isMobile || isFirefox;

    this.prebufferTime = this.getDefaultPrebufferMs() / 1000;
    if (isFirefoxMobile) {
      this.minScheduleAhead = 0.5; // 500ms ahead scheduling
      logger.debug('[🦊 FIREFOX MOBILE] Extra conservative buffering enabled (range: 800-3000ms)');
      logger.debug('[🦊 FIREFOX MOBILE] Using ScriptProcessorNode playback');
    } else if (isFirefox) {
      this.minScheduleAhead = 0.2; // Not used with ScriptProcessor but kept for consistency
      logger.debug('[🦊 FIREFOX DESKTOP] Using ScriptProcessorNode playback (avoids audio dropout issue)');
      logger.debug('[🦊 FIREFOX DESKTOP] Conservative buffering enabled (range: 800-3000ms)');
    } else if (this.isMobile) {
      this.minScheduleAhead = 0.3;
      logger.debug('[📱 MOBILE] Adaptive buffering enabled (range: 800-3000ms)');
    } else {
      this.minScheduleAhead = 0.1;
      logger.debug('[💻 DESKTOP] Adaptive buffering enabled (range: 800-3000ms)');
    }
  }

  /**
   * Register event listeners
   */
  on<K extends keyof PlaybackEvents>(event: K, callback: PlaybackEvents[K]): void {
    this.events[event] = callback;
  }

  /**
   * Remove event listeners
   */
  off<K extends keyof PlaybackEvents>(event: K): void {
    delete this.events[event];
  }

  /**
   * Record a single onaudioprocess callback duration. Allocation-free.
   * Reservoir holds the most recent N samples; we approximate p95 by sorting on
   * snapshot only (once per response, off the audio thread).
   */
  private recordCallbackDuration(durationMs: number, callbackStartNow: number): void {
    this.callbackCount++;
    this.callbackSumMs += durationMs;
    if (durationMs > this.callbackMaxMs) {
      this.callbackMaxMs = durationMs;
    }
    this.callbackReservoir[this.callbackReservoirIndex] = durationMs;
    this.callbackReservoirIndex = (this.callbackReservoirIndex + 1) % this.CALLBACK_RESERVOIR_SIZE;
    if (this.callbackReservoirCount < this.CALLBACK_RESERVOIR_SIZE) {
      this.callbackReservoirCount++;
    }

    // Inter-callback gap: how punctually the browser is invoking us. Independent of
    // how long our work takes. Big values here mean the audio thread is being preempted
    // even though our callback is cheap.
    if (this.lastCallbackAt > 0) {
      const gap = callbackStartNow - this.lastCallbackAt;
      this.interCallbackGapSumMs += gap;
      this.interCallbackGapCount++;
      if (gap > this.interCallbackGapMaxMs) this.interCallbackGapMaxMs = gap;
      this.interCallbackGapReservoir[this.interCallbackGapReservoirIndex] = gap;
      this.interCallbackGapReservoirIndex = (this.interCallbackGapReservoirIndex + 1) % this.CALLBACK_RESERVOIR_SIZE;
      if (this.interCallbackGapReservoirCount < this.CALLBACK_RESERVOIR_SIZE) {
        this.interCallbackGapReservoirCount++;
      }
    }
    this.lastCallbackAt = callbackStartNow;
  }

  private snapshotCallbackP95Ms(): number {
    return this.snapshotReservoirP95(this.callbackReservoir, this.callbackReservoirCount);
  }

  /** Compute p95 from a reservoir buffer. Off the audio thread (called once per response). */
  private snapshotReservoirP95(buf: Float32Array, count: number): number {
    if (count === 0) return 0;
    const samples = Array.from(buf.slice(0, count));
    samples.sort((a, b) => a - b);
    const idx = Math.min(samples.length - 1, Math.floor(samples.length * 0.95));
    return samples[idx];
  }

  private resetCallbackStats(): void {
    this.callbackCount = 0;
    this.callbackSumMs = 0;
    this.callbackMaxMs = 0;
    this.callbackReservoirCount = 0;
    this.callbackReservoirIndex = 0;
    this.lastCallbackAt = 0;
    this.interCallbackGapMaxMs = 0;
    this.interCallbackGapSumMs = 0;
    this.interCallbackGapCount = 0;
    this.interCallbackGapReservoirCount = 0;
    this.interCallbackGapReservoirIndex = 0;
    this.chunkAudioSamplesTotal = 0;
    this.chunkSizeMinSamples = Number.POSITIVE_INFINITY;
    this.chunkSizeMaxSamples = 0;
    this.chunkSizeSumSamples = 0;
    this.chunkSizeCount = 0;
    this.interChunkGapSumMs = 0;
    this.interChunkGapMaxMs = 0;
    this.interChunkGapCount = 0;
    this.interChunkGapReservoirCount = 0;
    this.interChunkGapReservoirIndex = 0;
    this.lastChunkArrivalAt = 0;
  }

  /**
   * Record one chunk arrival (PCM16 samples already converted to Float32).
   * Tracks chunk size + inter-chunk gap distributions.
   */
  private recordChunkArrival(numSamples: number, arrivalNow: number): void {
    this.chunkAudioSamplesTotal += numSamples;
    this.chunkSizeSumSamples += numSamples;
    this.chunkSizeCount++;
    if (numSamples < this.chunkSizeMinSamples) this.chunkSizeMinSamples = numSamples;
    if (numSamples > this.chunkSizeMaxSamples) this.chunkSizeMaxSamples = numSamples;

    if (this.lastChunkArrivalAt > 0) {
      const gap = arrivalNow - this.lastChunkArrivalAt;
      this.interChunkGapSumMs += gap;
      this.interChunkGapCount++;
      if (gap > this.interChunkGapMaxMs) this.interChunkGapMaxMs = gap;
      this.interChunkGapReservoir[this.interChunkGapReservoirIndex] = gap;
      this.interChunkGapReservoirIndex = (this.interChunkGapReservoirIndex + 1) % this.CALLBACK_RESERVOIR_SIZE;
      if (this.interChunkGapReservoirCount < this.CALLBACK_RESERVOIR_SIZE) {
        this.interChunkGapReservoirCount++;
      }
    }
    this.lastChunkArrivalAt = arrivalNow;
  }

  /**
   * 🎯 Monitor buffer health and adapt prebuffer size dynamically
   * This prevents clicks on poor network while minimizing latency on good network
   */
  private startBufferHealthMonitoring(): void {
    if (this.bufferHealthMonitor) {
      return; // Already monitoring
    }
    
    this.lastBufferCheckTime = Date.now();
    this.minBufferLevelSeen = 1.0;
    this.bufferStarvationCount = 0;
    this.timeInHealthyZone = 0;
    
    logger.debug('[🎯 ADAPTIVE] Buffer health monitoring started');
    
    // Check buffer health every 200ms.
    // Works for both playback paths: Chrome Desktop (streamingBuffer) and
    // Firefox/mobile (playbackRingBuffer). Previously gated on streamingBuffer only,
    // which meant Firefox/mobile never got reactive buffer growth or starvation tracking.
    this.bufferHealthMonitor = setInterval(() => {
      if (!this.playbackStarted) return;
      const hasChunks = this.useScriptProcessor
        ? this.chunkReadIndex < this.playbackRingBuffer.length
        : this.streamingBuffer.length > 0;
      if (!hasChunks) return;
      
      const now = Date.now();
      const deltaMs = now - this.lastBufferCheckTime;
      this.lastBufferCheckTime = now;
      
      // Calculate buffer health
      const SOURCE_SAMPLE_RATE = 24000;
      const currentBufferMs = (this.totalBufferedSamples / SOURCE_SAMPLE_RATE) * 1000;
      const targetBufferMs = this.prebufferTime * 1000;
      const bufferLevel = currentBufferMs / targetBufferMs; // 0.0 to 1.0+ (can exceed 1.0)
      
      // Track minimum seen
      this.minBufferLevelSeen = Math.min(this.minBufferLevelSeen, bufferLevel);
      this.responseStats.minBufferLevelSeen = Math.min(this.responseStats.minBufferLevelSeen, bufferLevel);

      // 🚨 CRITICAL: Buffer dangerously low (< 20%)
      if (bufferLevel < 0.2) {
        this.bufferStarvationCount++;
        this.responseStats.starvationCount++;
        this.timeInHealthyZone = 0;
        
        // Immediate reaction: increase buffer significantly
        const oldBuffer = this.prebufferTime;
        this.prebufferTime = Math.min(
          this.prebufferTime + (this.BUFFER_ADJUST_STEP * 3) / 1000, // +300ms jump
          this.getMaxBufferMs() / 1000
        );
        
        if (this.prebufferTime !== oldBuffer) {
          logger.debug(`[🎯 ADAPTIVE] 🚨 STARVATION! Increased buffer: ${(oldBuffer * 1000).toFixed(0)}ms → ${(this.prebufferTime * 1000).toFixed(0)}ms (level: ${(bufferLevel * 100).toFixed(1)}%)`);
        }
      }
      // ⚠️ WARNING: Buffer low (< 50%)
      else if (bufferLevel < 0.5) {
        this.timeInHealthyZone = 0;
        
        // Gradual increase
        const oldBuffer = this.prebufferTime;
        this.prebufferTime = Math.min(
          this.prebufferTime + this.BUFFER_ADJUST_STEP / 1000, // +100ms
          this.getMaxBufferMs() / 1000
        );
        
        if (this.prebufferTime !== oldBuffer) {
          logger.debug(`[🎯 ADAPTIVE] ⚠️ LOW BUFFER. Increased: ${(oldBuffer * 1000).toFixed(0)}ms → ${(this.prebufferTime * 1000).toFixed(0)}ms (level: ${(bufferLevel * 100).toFixed(1)}%)`);
        }
      }
      // ✅ HEALTHY: Buffer good (50-100%)
      else if (bufferLevel >= 0.5 && bufferLevel <= 1.0) {
        this.timeInHealthyZone += deltaMs;
        // Stay at current level (Goldilocks zone)
      }
      // 📊 OVER-BUFFERED: Buffer too high (> 100%)
      else if (bufferLevel > 1.0) {
        this.timeInHealthyZone += deltaMs;

        // Two-tier reduction: react fast on mobile because over-buffering
        // desyncs the browser's AEC and leaks the avatar's voice into the mic.
        // Desktop browsers wait 30s of healthy over-buffering as before — they
        // don't suffer the same AEC drift.
        const aggressiveShrink = this.isMobile && bufferLevel > 2.0;
        const shouldShrink = aggressiveShrink || this.timeInHealthyZone > 30000;

        if (shouldShrink) {
          const oldBuffer = this.prebufferTime;
          // Shrink harder when severely over-buffered (Firefox Mobile + level > 2)
          const shrinkBy = aggressiveShrink
            ? (this.BUFFER_ADJUST_STEP * 3) / 1000 // -300ms jump
            : this.BUFFER_ADJUST_STEP / 1000;       // -100ms gradual
          this.prebufferTime = Math.max(
            this.prebufferTime - shrinkBy,
            this.getMinBufferMs() / 1000
          );

          if (this.prebufferTime !== oldBuffer) {
            logger.debug(`[🎯 ADAPTIVE] 📉 Reducing buffer (over-buffered): ${(oldBuffer * 1000).toFixed(0)}ms → ${(this.prebufferTime * 1000).toFixed(0)}ms (level: ${(bufferLevel * 100).toFixed(1)}%)${aggressiveShrink ? ' [aggressive]' : ''}`);
            this.timeInHealthyZone = 0; // Reset timer after adjustment
          }
        }
      }
      
      // Recalculate prebuffer samples for next chunk
      this.prebufferSamples = Math.floor(this.prebufferTime * SOURCE_SAMPLE_RATE);
      
    }, 200); // Check every 200ms
  }

  /**
   * Stop buffer health monitoring
   */
  private stopBufferHealthMonitoring(): void {
    if (this.bufferHealthMonitor) {
      clearInterval(this.bufferHealthMonitor);
      this.bufferHealthMonitor = null;

      // Log final stats
      logger.debug(`[🎯 ADAPTIVE] Monitoring stopped. Final buffer: ${(this.prebufferTime * 1000).toFixed(0)}ms, Min level seen: ${(this.minBufferLevelSeen * 100).toFixed(1)}%, Starvations: ${this.bufferStarvationCount}`);
    }
  }

  /**
   * 🎯 Detect network jitter and adjust prebuffer dynamically
   * This prevents clicks during network congestion by increasing buffer ahead of time
   */
  private detectAndAdaptToJitter(currentTime: number): void {
    // Track chunk arrival times
    this.chunkArrivalTimes.push(currentTime);

    // Keep only last N samples
    if (this.chunkArrivalTimes.length > this.MAX_LATENCY_SAMPLES) {
      this.chunkArrivalTimes.shift();
    }

    // Need at least 3 samples to calculate jitter
    if (this.chunkArrivalTimes.length >= 3) {
      // Calculate inter-chunk latencies
      const latencies: number[] = [];
      for (let i = 1; i < this.chunkArrivalTimes.length; i++) {
        latencies.push(this.chunkArrivalTimes[i] - this.chunkArrivalTimes[i - 1]);
      }

      // Calculate rolling average latency
      const newAverageLatency = latencies.reduce((a, b) => a + b, 0) / latencies.length;

      // Update rolling average (exponential smoothing)
      if (this.averageInterChunkLatency === 0) {
        this.averageInterChunkLatency = newAverageLatency;
      } else {
        this.averageInterChunkLatency = this.averageInterChunkLatency * 0.7 + newAverageLatency * 0.3;
      }

      // Calculate jitter (standard deviation of latencies)
      const mean = latencies.reduce((a, b) => a + b, 0) / latencies.length;
      const variance = latencies.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / latencies.length;
      const jitter = Math.sqrt(variance);

      // Detect significant jitter (> 50ms variation)
      const wasJittery = this.jitterDetected;
      this.jitterDetected = jitter > 50;

      // Log jitter detection
      if (this.jitterDetected && !wasJittery) {
        logger.debug(`[🎯 JITTER] ⚠️ Network jitter detected! Latency: ${(this.averageInterChunkLatency).toFixed(0)}ms, Jitter: ${jitter.toFixed(0)}ms`);
      } else if (!this.jitterDetected && wasJittery) {
        logger.debug(`[🎯 JITTER] ✅ Network stabilized. Latency: ${(this.averageInterChunkLatency).toFixed(0)}ms`);
      }

      // 🎯 Adaptive prebuffer based on jitter
      if (this.jitterDetected) {
        // Increase buffer to handle jitter
        const targetBuffer = Math.min(
          this.prebufferTime + (jitter / 1000) * 2, // Add 2x jitter as buffer
          this.getMaxBufferMs() / 1000
        );

        if (targetBuffer !== this.prebufferTime) {
          logger.debug(`[🎯 JITTER] 📈 Increasing buffer for jitter: ${(this.prebufferTime * 1000).toFixed(0)}ms → ${(targetBuffer * 1000).toFixed(0)}ms`);
          this.prebufferTime = targetBuffer;
        }
      } else if (!this.jitterDetected && wasJittery) {
        // Gradually reduce buffer when network stabilizes
        const targetBuffer = Math.max(
          this.prebufferTime - 0.1, // Reduce by 100ms when stable
          this.getMinBufferMs() / 1000
        );

        if (targetBuffer !== this.prebufferTime) {
          logger.debug(`[🎯 JITTER] 📉 Reducing buffer (network stable): ${(this.prebufferTime * 1000).toFixed(0)}ms → ${(targetBuffer * 1000).toFixed(0)}ms`);
          this.prebufferTime = targetBuffer;
        }
      }

      // Recalculate samples based on new buffer size
      this.prebufferSamples = Math.floor(this.prebufferTime * 24000);
    }
  }

  /**
   * Check if playback is active
   */
  isPlaybackActive(): boolean {
    return this.isPlaying;
  }

  /**
   * Check if streaming playback is still active (has scheduled nodes or buffer)
   * This is more accurate than isPlaying for detecting if audio is still in buffer
   */
  isStreamingPlaybackActive(): boolean {
    // ScriptProcessor (Mobile + Firefox): Check if playback started or ring buffer has unread chunks
    if (this.useScriptProcessor) {
      const hasUnreadChunks = this.chunkReadIndex < this.playbackRingBuffer.length;
      return this.playbackStarted || hasUnreadChunks || this.totalBufferedSamples > 0;
    }
    // AudioBufferSourceNode (Chrome Desktop): Check if there are scheduled nodes or buffer has chunks
    return this.playbackStarted || this.scheduledNodes.length > 0 || this.streamingBuffer.length > 0;
  }

  /**
   * Diagnostics snapshot for the on-screen audio diagnostics overlay.
   * Cheap to call (~once per 250ms); reads internal state, no allocations beyond the object.
   */
  getPlaybackDiagnostics() {
    const now = this.audioContext.currentTime;
    // Lookahead = how far in the future the next chunk is scheduled. If it grows past
    // ~600ms while playback is active, the avatar is being heard with that much delay,
    // and the VAD's playbackActive boost is firing too early relative to what the user hears.
    const scheduleLookaheadMs = this.nextScheduledTime > now
      ? (this.nextScheduledTime - now) * 1000
      : 0;
    // bufferLevel = current buffered ms / target prebuffer ms. < 1 means the
    // buffer is below target (jitter risk); < 0.2 is "starvation"; values
    // between 0.2 and 0.5 are "near-misses" that can produce audible clicks
    // without ever crossing the starvation threshold.
    const SOURCE_SR = 24000;
    const currentBufferedMs = (this.totalBufferedSamples / SOURCE_SR) * 1000;
    const targetMs = this.prebufferTime * 1000;
    const bufferLevel = targetMs > 0 ? currentBufferedMs / targetMs : 0;
    return {
      isPlaying: this.isPlaying,
      streamingActive: this.isStreamingPlaybackActive(),
      scheduledNodes: this.scheduledNodes.length,
      bufferedChunks: this.streamingBuffer.length,
      bufferedSamples: this.totalBufferedSamples,
      scheduleLookaheadMs,
      useScriptProcessor: this.useScriptProcessor,
      bufferStarvationCount: this.bufferStarvationCount,
      // Adaptive buffering state: if these grow during a session, the playback
      // pipeline is increasing its end-to-end delay — that's the drift hypothesis
      // for why AEC stops cancelling correctly mid-conversation.
      prebufferTimeMs: this.prebufferTime * 1000,
      learnedPrebufferMs: this.learnedPrebufferMs,
      averageInterChunkLatencyMs: this.averageInterChunkLatency,
      jitterDetected: this.jitterDetected,
      bufferLevel,
      // Cumulative network-quality signal for the on-screen pill. Aggregates the last
      // ~5 responses (within a 60s window) so the indicator reflects sustained pressure
      // across conversational turns, not the transient state at poll time — which drops
      // to baseline whenever no chunks are arriving.
      networkQuality: this.snapshotNetworkQuality(),
    };
  }

  /**
   * 🎯 Text-audio sync: Returns accumulated playback time in milliseconds.
   * Only advances while audio is actively being scheduled/drained — naturally
   * freezes during pause and buffer underruns. Used by useSyncedTextReveal
   * to throttle text reveal proportionally to audio playback position.
   *
   * Two paths:
   * - ScriptProcessor (Mobile/Firefox): uses accumulatedPlayedMs, which is
   *   incremented in the onaudioprocess callback as samples are drained —
   *   this is already real-time (samples leave the buffer as they are heard).
   * - AudioBufferSourceNode (Chrome Desktop): computes elapsed from the real
   *   audio clock (audioContext.currentTime - playbackStartContextTime) so it
   *   tracks what the user actually HEARS, not what has been scheduled ahead.
   *   Chunks are scheduled minScheduleAhead in the future; accumulating their
   *   durations at schedule time would race ahead of real playback and cause
   *   the karaoke text to reveal all at once while audio is still playing.
   *   When not actively playing (paused / between responses), returns the
   *   frozen accumulatedRealPlayedMs so the value doesn't jump backward.
   */
  getElapsedPlaybackMs(): number {
    // ScriptProcessor path: real-time accumulation in the audio callback
    if (this.useScriptProcessor) {
      return this.accumulatedPlayedMs;
    }
    // AudioBufferSourceNode path: real audio clock
    if (this.isPlaying && this.playbackStartContextTime > 0) {
      const elapsedSec = this.audioContext.currentTime - this.playbackStartContextTime;
      return Math.max(0, this.accumulatedRealPlayedMs + elapsedSec * 1000);
    }
    // Not actively playing — return frozen accumulated value
    return this.accumulatedRealPlayedMs;
  }

  /**
   * 🎯 Text-audio sync: Returns estimated total audio duration in milliseconds
   * for the current response, based on total samples received so far.
   */
  getTotalAudioDurationMs(): number {
    return (this.totalSamplesReceived / 24000) * 1000;
  }

  /**
   * 🎯 Text-audio sync (AudioBufferSourceNode path): Freeze the real elapsed
   * playback value at the current audio clock position. Called when playback
   * stops or pauses so getElapsedPlaybackMs() returns a stable value instead
   * of continuing to advance (or jumping backward to 0).
   */
  private freezeRealPlayedMs(): void {
    if (this.playbackStartContextTime > 0) {
      // Add the elapsed real-playback time since (re)start to the frozen baseline.
      // Clamp at 0 in case of clock skew. Then clear the start anchor so the value
      // stays frozen until the next chunk is scheduled (which re-anchors it).
      const elapsedSec = Math.max(0, this.audioContext.currentTime - this.playbackStartContextTime);
      this.accumulatedRealPlayedMs += elapsedSec * 1000;
      this.playbackStartContextTime = 0;
    }
  }

  /**
   * Aggregate signal for the NetworkQualityPill. Counts responses in the last 60s and
   * how many of them had REAL AUDIBLE PROBLEMS (clicks, gaps, artefacts the user hears).
   *
   * IMPORTANT — relaxed thresholds (2026-02):
   *   The pill used to trigger on internal warnings (starvation counter bumps,
   *   jitter > 50ms, interChunkGap > 250ms) that the user never actually hears because
   *   the adaptive buffer recovers in time. This caused the pill to appear too often
   *   with no perceptible audio issue.
   *   Now we only count what IS audible:
   *     • scheduleUnderrunCount > 0  — the AudioBufferSourceNode scheduler ran dry;
   *       a real silent gap of `scheduleUnderrun*Ms` was heard.
   *     • starvationCount >= 3 on a SINGLE response — a single < 20% buffer dip is
   *       a warning the buffer recovers from; 3+ in 500ms of monitoring means the
   *       buffer was starved for a sustained period, which translates into clicks.
   *   Jitter and slow chunks are NO LONGER counted as "degraded" — they are internal
   *   telemetry, not user-facing artefacts.
   *
   * The pill itself applies rising-edge + auto-dismiss + cooldown hysteresis on top.
   */
  private snapshotNetworkQuality(): {
    recentResponseCount: number;
    recentDegradedResponseCount: number;
    recentMaxPrebufferMs: number;
    recentAnyJitter: boolean;
    recentAnyAudibleGap: boolean;
  } {
    const now = performance.now();
    const WINDOW_MS = 60_000;
    let count = 0;
    let degraded = 0;
    let maxPrebuffer = 0;
    let anyJitter = false;
    let anyAudibleGap = false;
    for (const r of this.recentResponseHealth) {
      if (now - r.finishedAt > WINDOW_MS) continue;
      count++;
      if (r.endPrebufferMs > maxPrebuffer) maxPrebuffer = r.endPrebufferMs;
      if (r.jitterEverDetected) anyJitter = true;
      // Audible gap: a scheduling underrun is always audible (silent gap inserted).
      // Starvation only counts if it happened 3+ times during one response — that
      // means the buffer stayed below 20% for ~600ms of monitoring, producing clicks.
      // A single starvation bump is a near-miss the buffer recovered from silently.
      const hadAudibleGap = r.scheduleUnderrunCount > 0 || r.starvationCount >= 3;
      if (hadAudibleGap) anyAudibleGap = true;
      // "Degraded" now means the same as "had an audible gap" — the pill should
      // only surface problems the user can actually hear.
      if (hadAudibleGap) {
        degraded++;
      }
    }
    return {
      recentResponseCount: count,
      recentDegradedResponseCount: degraded,
      recentMaxPrebufferMs: maxPrebuffer,
      recentAnyJitter: anyJitter,
      recentAnyAudibleGap: anyAudibleGap,
    };
  }

  /**
   * Pause avatar voice output (immediately stops audio playback)
   */
  pausePlayback(): void {
    try {
      if (this.isPaused) {
        logger.debug('[🔊 PAUSE] Already paused');
        return;
      }

      this.isPaused = true;
      logger.debug('[🔊 PAUSE] 🛑 Pausing audio playback...');

      // 🎯 Text-audio sync (AudioBufferSourceNode path): Freeze the real elapsed
      // value so getElapsedPlaybackMs() stops advancing while paused. On resume,
      // playbackStartContextTime is re-anchored so the clock continues correctly.
      this.freezeRealPlayedMs();

      // 💻 DESKTOP: Stop all scheduled nodes that haven't started playing yet
      // Note: Nodes that already started playing will continue (can't pause them in Web Audio API)
      // but we'll stop scheduling new ones
      const nodesToStop: AudioBufferSourceNode[] = [];
      this.scheduledNodes.forEach(node => {
        try {
          // Check if node has started (very rough heuristic - once scheduled, assume it might start soon)
          // We'll stop all scheduled nodes to be safe
          nodesToStop.push(node);
        } catch (e) {
          // Ignore errors
        }
      });

      // Stop all scheduled nodes
      nodesToStop.forEach(node => {
        try {
          node.stop();
          node.disconnect();
          
          // Clean up pooled nodes
          const pooledNodes = this.activePooledNodes.get(node);
          if (pooledNodes) {
            pooledNodes.gain.disconnect();
            if (pooledNodes.fromPool) {
              this.nodePool.returnGain(pooledNodes.gain);
            }
            this.activePooledNodes.delete(node);
          }
          
          // Remove from scheduled nodes
          const index = this.scheduledNodes.indexOf(node);
          if (index > -1) {
            this.scheduledNodes.splice(index, 1);
          }
        } catch (e) {
          // Ignore errors from already-stopped nodes
        }
      });

      // 💻 DESKTOP: Save unscheduled chunks for resume
      this.pausedChunks = [...this.streamingBuffer];
      this.streamingBuffer = [];
      this.pausedNextScheduledTime = this.nextScheduledTime;
      this.pausedTotalBufferedSamples = this.totalBufferedSamples;
      this.totalBufferedSamples = 0;

      // 📱 ScriptProcessor (Mobile + Firefox): Save ring buffer state for resume
      if (this.useScriptProcessor && this.chunkReadIndex < this.playbackRingBuffer.length) {
        // Compact away already-consumed chunks so pausedRingBuffer[0] is the chunk
        // we were draining when paused — matches the contract resumePlayback expects.
        if (this.chunkReadIndex > 0) {
          this.playbackRingBuffer.splice(0, this.chunkReadIndex);
          this.chunkReadIndex = 0;
        }
        this.pausedRingBuffer = [...this.playbackRingBuffer];
        this.pausedBufferReadIndex = this.playbackBufferReadIndex;
        this.playbackRingBuffer = [];
        this.playbackBufferReadIndex = 0;
        this.pausedTotalBufferedSamples += this.totalBufferedSamples;
        this.totalBufferedSamples = 0;
        
        // Disconnect ScriptProcessorNode to stop playback
        if (this.playbackProcessor) {
          this.playbackProcessor.disconnect();
          this.playbackProcessor.onaudioprocess = null;
          this.playbackProcessor = null;
        }
      }

      // Stop buffer health monitoring so it doesn't record starvation events
      // during an intentional pause (which would falsely trigger the slow-network pill).
      this.stopBufferHealthMonitoring();

      // Mute master gain as backup (in case any nodes are still playing)
      if (this.masterGainNode) {
        this.savedVolume = this.masterGainNode.gain.value;
        this.masterGainNode.gain.cancelScheduledValues(this.audioContext.currentTime);
        this.masterGainNode.gain.setValueAtTime(this.masterGainNode.gain.value, this.audioContext.currentTime);
        this.masterGainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 0.01); // Quick fade
      }

      // Mute existing HTML audio elements as backup
      this.playbackAudioElements.forEach((audioElement) => {
        audioElement.pause();
      });

      logger.debug('[🔊 PAUSE] ✅ Audio paused:', {
        stoppedNodes: nodesToStop.length,
        savedChunks: this.pausedChunks.length,
        savedRingBufferChunks: this.pausedRingBuffer.length,
        savedTotalSamples: this.pausedTotalBufferedSamples
      });
    } catch (error) {
      console.error('[🔊 PAUSE] ❌ Error pausing playback:', error);
      this.events.onError?.(error as Error);
    }
  }

  /**
   * Resume avatar voice output (continues from where it was paused)
   */
  resumePlayback(): void {
    try {
      if (!this.isPaused) {
        logger.debug('[🔊 PAUSE] Not paused');
        return;
      }

      this.isPaused = false;
      logger.debug('[🔊 PAUSE] ▶️ Resuming audio playback...');

      // Restore master gain to saved volume (0 is valid for muted state)
      if (this.masterGainNode) {
        // savedVolume is always valid (initialized to 1.0, updated in setVolume and pause)
        const targetVolume = this.savedVolume;
        this.masterGainNode.gain.cancelScheduledValues(this.audioContext.currentTime);
        this.masterGainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
        this.masterGainNode.gain.linearRampToValueAtTime(targetVolume, this.audioContext.currentTime + 0.01); // Quick fade
      }

      // 💻 DESKTOP: Restore unscheduled chunks and continue scheduling
      if (this.pausedChunks.length > 0) {
        this.streamingBuffer = [...this.pausedChunks];
        this.totalBufferedSamples = this.pausedTotalBufferedSamples;
        this.nextScheduledTime = this.pausedNextScheduledTime;
        
        // Ensure analyser exists
        if (!this.playbackAnalyserNode) {
          this.playbackAnalyserNode = this.audioContext.createAnalyser();
          this.playbackAnalyserNode.fftSize = 512;
          this.playbackAnalyserNode.smoothingTimeConstant = 0.3;
          this.playbackAnalyserNode.minDecibels = -90;
          this.playbackAnalyserNode.maxDecibels = -10;
        }
        
        this.startBufferHealthMonitoring();
        
        // Adjust nextScheduledTime to be safe (at least minScheduleAhead in future)
        const now = this.audioContext.currentTime;
        if (this.nextScheduledTime < now + this.minScheduleAhead) {
          this.nextScheduledTime = now + this.minScheduleAhead;
        }
        
        // Always restart playback if we have chunks to restore
        this.playbackStarted = true;
        this.isPlaying = true;
        this.scheduleNextChunk();
        
        this.pausedChunks = [];
        this.pausedTotalBufferedSamples = 0;
      }

      // 📱 ScriptProcessor (Mobile + Firefox): Restore ring buffer and restart ScriptProcessorNode
      if (this.useScriptProcessor && this.pausedRingBuffer.length > 0) {
        this.playbackRingBuffer = [...this.pausedRingBuffer];
        this.playbackBufferReadIndex = this.pausedBufferReadIndex;
        this.chunkReadIndex = 0; // pausedRingBuffer was compacted on pause, so index 0 is the current chunk
        this.totalBufferedSamples = this.pausedTotalBufferedSamples;
        
        // Ensure analyser exists
        if (!this.playbackAnalyserNode) {
          this.playbackAnalyserNode = this.audioContext.createAnalyser();
          this.playbackAnalyserNode.fftSize = 512;
          this.playbackAnalyserNode.smoothingTimeConstant = 0.3;
          this.playbackAnalyserNode.minDecibels = -90;
          this.playbackAnalyserNode.maxDecibels = -10;
        }
        
        // Always restart playback if we have chunks to restore
        this.playbackStarted = true;
        this.isPlaying = true;
        this.startScriptProcessorPlayback();
        
        this.pausedRingBuffer = [];
        this.pausedBufferReadIndex = 0;
        this.pausedTotalBufferedSamples = 0;
      }

      // Resume existing HTML audio elements
      if (this.playbackAudioElements.size > 0) {
        this.playbackAudioElements.forEach((audioElement) => {
          if (audioElement.paused) {
            audioElement.play().catch(err => {
              console.warn('[🔊 PAUSE] ⚠️ Could not resume audio element:', err);
            });
          }
        });
      }

      logger.debug('[🔊 PAUSE] ✅ Audio resumed:', {
        restoredChunks: this.pausedChunks.length,
        restoredRingBufferChunks: this.pausedRingBuffer.length,
        playbackStarted: this.playbackStarted
      });
    } catch (error) {
      console.error('[🔊 PAUSE] ❌ Error resuming playback:', error);
      this.events.onError?.(error as Error);
    }
  }

  /**
   * Ensure masterGainNode is created with the correct value based on playback path.
   * This centralizes creation to avoid inconsistent initialization.
   * 
   * - ScriptProcessor path: masterGainNode = 1.0 (amplification at sample level)
   * - AudioBufferSourceNode path: masterGainNode = config.volume (amplification via GainNode)
   */
  private ensureMasterGainNode(): void {
    if (this.masterGainNode) return; // Already created
    
    // Ensure analyser exists first
    if (!this.playbackAnalyserNode) {
      this.playbackAnalyserNode = this.audioContext.createAnalyser();
      this.playbackAnalyserNode.fftSize = 512;
      this.playbackAnalyserNode.smoothingTimeConstant = 0.3;
      this.playbackAnalyserNode.minDecibels = -90;
      this.playbackAnalyserNode.maxDecibels = -10;
    }
    
    // Create masterGainNode with path-appropriate value
    this.masterGainNode = this.audioContext.createGain();
    
    if (this.useScriptProcessor) {
      // ScriptProcessor: amplification at sample level, masterGain = 1.0
      this.masterGainNode.gain.value = 1.0;
      logger.debug('[🔊 INIT] masterGainNode created with gain=1.0 (ScriptProcessor path)');
    } else {
      // AudioBufferSourceNode: amplification via masterGainNode
      this.masterGainNode.gain.value = this.config.volume ?? 1.0;
      logger.debug(`[🔊 INIT] masterGainNode created with gain=${this.config.volume ?? 1.0} (AudioBufferSourceNode path)`);
    }
    
    this.savedVolume = this.config.volume ?? 1.0;

    // Dynamics compressor — normalizes loudness across OpenAI voices, but is also one of
    // the only non-passthrough nodes in our chain and a candidate source of the boundary
    // clicks reported on desktop in run qt_1777847305251_97ce4j (telemetry showed 0
    // scheduling underruns and minBufferLevelPct=100% in all 30 turns, ruling out the
    // buffer/scheduling layer). Firefox already bypasses it (audible improvement). This
    // is the same experiment for Chrome/Edge/Safari: if clicks disappear, the compressor
    // is the source. To restore, set BYPASS_COMPRESSOR back to false.
    // Per-utterance peak normalization gain. Sits before the analyser so the
    // visualization waveform also reflects the normalized signal.
    this.normalizationGainNode = this.audioContext.createGain();
    this.normalizationGainNode.gain.value = 1.0;

    const BYPASS_COMPRESSOR = true;
    if (BYPASS_COMPRESSOR) {
      this.masterGainNode.connect(this.normalizationGainNode);
      this.normalizationGainNode.connect(this.playbackAnalyserNode);
      this.playbackAnalyserNode.connect(this.audioContext.destination);
      logger.debug('[🔊 INIT] Master chain WITHOUT compressor (masterGain → normalizationGain → analyser → destination)');
    } else {
      this.compressorNode = this.audioContext.createDynamicsCompressor();
      this.compressorNode.threshold.value = -24;
      this.compressorNode.knee.value = 12;
      this.compressorNode.ratio.value = 4;
      this.compressorNode.attack.value = 0.003;
      this.compressorNode.release.value = 0.15;
      this.masterGainNode.connect(this.compressorNode);
      this.compressorNode.connect(this.normalizationGainNode);
      this.normalizationGainNode.connect(this.playbackAnalyserNode);
      this.playbackAnalyserNode.connect(this.audioContext.destination);
      logger.debug('[🔊 INIT] Master audio chain connected: masterGain → compressor → normalizationGain → analyser → destination');
    }
  }

  /**
   * 📱 MOBILE/Firefox: Start (or resume) ScriptProcessorNode playback.
   *
   * The processor is created once and kept alive for the whole session: on Firefox mobile
   * creating/destroying a ScriptProcessorNode between every response was causing enough GC
   * pressure to glitch the audio callback by the 3rd turn. When the silence timeout fires
   * we now just mark the processor idle — its callback keeps running but writes silence
   * until the next response refills the ring buffer. The processor is only truly destroyed
   * on interrupt / full reset / pause.
   */
  private startScriptProcessorPlayback(): void {
    this.responseCount++;
    this.playbackStarted = true;
    this.silenceCounter = 0;

    // If the processor already exists (carried over from a previous response), just emit
    // the start event and let it keep running. No allocation, no connect, no GC churn.
    if (this.playbackProcessor) {
      logger.debug('[📱 MOBILE] Resuming existing ScriptProcessorNode (session-long)');
      this.isPlaying = true;
      this.events.onPlaybackStart?.();
      return;
    }

    logger.debug('[📱 MOBILE] Creating ScriptProcessorNode (one-time for this session)');

    // Create analyser for waveform (may be created by ensureMasterGainNode)
    if (!this.playbackAnalyserNode) {
      this.playbackAnalyserNode = this.audioContext.createAnalyser();
      this.playbackAnalyserNode.fftSize = 512;
      this.playbackAnalyserNode.smoothingTimeConstant = 0.3;
      this.playbackAnalyserNode.minDecibels = -90;
      this.playbackAnalyserNode.maxDecibels = -10;
    }

    // Create ScriptProcessorNode (4096 buffer size, mono in/out)
    const bufferSize = 4096;
    this.playbackProcessor = this.audioContext.createScriptProcessor(bufferSize, 1, 1);
    
    const self = this;
    const sourceSampleRate = 24000; // OpenAI sends 24kHz
    const targetSampleRate = this.audioContext.sampleRate; // Browser native (usually 48kHz)
    const sampleRateRatio = targetSampleRate / sourceSampleRate; // e.g., 48000/24000 = 2.0
    
    logger.debug(`[📱 MOBILE] Resampling: ${sourceSampleRate}Hz → ${targetSampleRate}Hz (ratio: ${sampleRateRatio.toFixed(2)})`);
    
    this.playbackProcessor.onaudioprocess = (e) => {
      const callbackStart = performance.now();
      const outputData = e.outputBuffer.getChannelData(0);
      const outputLength = outputData.length;

      // Calculate how many source samples we need
      const sourceSamplesNeeded = Math.ceil(outputLength / sampleRateRatio);

      // Grow scratch buffer if a platform surprised us with a larger callback size
      if (self.sourceSamplesBuffer.length < sourceSamplesNeeded) {
        self.sourceSamplesBuffer = new Float32Array(sourceSamplesNeeded);
      }
      const sourceSamples = self.sourceSamplesBuffer;
      let sourceSamplesLength = 0;

      // Drain source samples from the ring buffer using an advancing cursor
      // (chunkReadIndex) rather than Array.shift(), which is O(n) and was
      // measurably costing jitter on Firefox desktop with a deep buffer.
      while (sourceSamplesLength < sourceSamplesNeeded && self.chunkReadIndex < self.playbackRingBuffer.length) {
        const currentChunk = self.playbackRingBuffer[self.chunkReadIndex];
        const remainingInChunk = currentChunk.length - self.playbackBufferReadIndex;
        const samplesToTake = Math.min(remainingInChunk, sourceSamplesNeeded - sourceSamplesLength);

        for (let i = 0; i < samplesToTake; i++) {
          sourceSamples[sourceSamplesLength++] = currentChunk[self.playbackBufferReadIndex++];
        }

        if (self.playbackBufferReadIndex >= currentChunk.length) {
          self.chunkReadIndex++;
          self.playbackBufferReadIndex = 0;
          self.totalBufferedSamples -= currentChunk.length;
        }
      }

      // 🎯 Text-audio sync: accumulate played time (advances only when samples are drained)
      if (sourceSamplesLength > 0) {
        self.accumulatedPlayedMs += (sourceSamplesLength / 24000) * 1000;
      }

      // Lazy compaction: once far enough behind, splice off consumed chunks so the
      // array doesn't grow unbounded over long conversations. splice(0,n) is O(n)
      // but it runs at most every ~12s at 400ms chunks — well outside the hot path.
      if (self.chunkReadIndex >= self.CHUNK_COMPACT_THRESHOLD ||
          (self.chunkReadIndex > 0 && self.chunkReadIndex === self.playbackRingBuffer.length)) {
        self.playbackRingBuffer.splice(0, self.chunkReadIndex);
        self.chunkReadIndex = 0;
      }

      // Resample and fill output buffer
      // Apply the configured volume directly to samples (default 1.0 = pass-through).
      const amplification = self.config.volume ?? 1.0;

      if (sourceSamplesLength >= 2) {
        // Upsample using linear interpolation (24kHz → 48kHz)
        for (let i = 0; i < outputLength; i++) {
          const srcIndex = i / sampleRateRatio;
          const srcIndexFloor = Math.floor(srcIndex);
          const srcIndexCeil = Math.min(srcIndexFloor + 1, sourceSamplesLength - 1);
          const t = srcIndex - srcIndexFloor;

          if (srcIndexFloor < sourceSamplesLength) {
            const sample = sourceSamples[srcIndexFloor] * (1 - t) + sourceSamples[srcIndexCeil] * t;
            // Clamp to prevent clipping distortion (max ±1.0)
            outputData[i] = Math.max(-1.0, Math.min(1.0, sample * amplification));
          } else {
            outputData[i] = 0; // Silence if we run out
          }
        }
      } else {
        // Not enough samples - fill with silence
        for (let i = 0; i < outputLength; i++) {
          outputData[i] = 0;
        }
      }

      // 📝 Check if buffer is empty - mark processor idle after sustained silence
      const ringHasData = self.chunkReadIndex < self.playbackRingBuffer.length;
      if (!ringHasData && sourceSamplesLength === 0) {
        // Only count silence while we're actively playing a response. Once idle between
        // responses the processor keeps running but we stop counting (nothing to do).
        if (!self.playbackStarted) {
          // stays at 0; waiting for the next response to arrive
        } else {
          if (!self.silenceCounter) self.silenceCounter = 0;
          self.silenceCounter++;
        }

        // Silence threshold expressed in ms (sample-rate independent). Previously hard-coded
        // as a callback count assuming 85ms/callback at 48kHz; with Firefox now running at
        // 24kHz the same count would mean 2× the wallclock delay.
        // - responseFinished=true: chunks can still arrive ~700ms after audio.done on bad
        //   networks; wait ~680ms before declaring end.
        // - responseFinished=false: mid-response; allow ~1020ms gap to cover WebSocket stalls.
        // Turn-taking is NOT gated on onPlaybackStop, so the extra tail latency is cosmetic.
        const callbackMs = (outputLength / targetSampleRate) * 1000;
        const silenceThresholdMs = self.responseFinished ? 680 : 1020;

        if (self.silenceCounter * callbackMs > silenceThresholdMs && self.playbackStarted) {
          const delayMs = Math.round(self.silenceCounter * callbackMs);
          logger.debug(`[📱 MOBILE] Buffer empty for ~${delayMs}ms - marking idle (responseFinished: ${self.responseFinished})`);
          // Don't destroy the processor here — it would cause GC churn if we recreate it
          // next turn. Just mark it idle; the callback keeps running and writing silence
          // until the next response's chunks arrive.
          self.markScriptProcessorIdle();
        }
      } else {
        // Reset silence counter when we have audio
        self.silenceCounter = 0;
      }

      // 🎯 TELEMETRY: capture how long this callback took. Allocation-free; the only
      // cost in the hot path is one performance.now() pair plus a couple of writes.
      // Whether this duration grows turn-over-turn is the key signal that distinguishes
      // GC pressure / node aging from network starvation as the cause of the collapse.
      self.recordCallbackDuration(performance.now() - callbackStart, callbackStart);
    };

    // Connect: ScriptProcessor → Master Gain → Analyser → Destination
    // Use centralized method to ensure consistent masterGainNode initialization
    this.ensureMasterGainNode();
    
    // Connect processor to masterGain (master chain already connected by ensureMasterGainNode)
    this.playbackProcessor.connect(this.masterGainNode!);
    
    this.isPlaying = true;
    this.events.onPlaybackStart?.();
  }

  /**
   * Mark the session-long ScriptProcessorNode as idle (current response finished),
   * without destroying it. The callback keeps running and emits silence until the next
   * response adds chunks to the ring buffer. This avoids Firefox-mobile GC churn from
   * repeatedly creating/disposing ScriptProcessorNodes.
   */
  private markScriptProcessorIdle(): void {
    this.playbackStarted = false;
    this.isPlaying = false;
    this.silenceCounter = 0;
    this.events.onPlaybackStop?.();
    this.logResponseHealth('complete');
    logger.debug('[📱 MOBILE] ScriptProcessorNode idled (processor kept alive)');
  }

  /**
   * Fully tear down the ScriptProcessorNode. Used only on interrupt / full reset / pause.
   * Between responses, prefer markScriptProcessorIdle().
   */
  private stopScriptProcessorPlayback(): void {
    if (this.playbackProcessor) {
      this.playbackProcessor.disconnect();
      this.playbackProcessor.onaudioprocess = null;
      this.playbackProcessor = null;
    }

    this.playbackStarted = false;
    this.isPlaying = false;
    this.events.onPlaybackStop?.();
    this.logResponseHealth('complete');

    logger.debug('[📱 MOBILE] ScriptProcessorNode destroyed');
  }

  /**
   * Sample the inter-arrival gap for the first few chunks of a response, and trigger a
   * one-shot health evaluation once we have enough samples.
   *
   * Why not wait for detectAndAdaptToJitter's reactive path? It only kicks in after ≥3
   * chunks AND requires measurable variance; a slow-but-steady connection shows no jitter
   * and starvation only triggers after the buffer has already run dry. This path bumps the
   * prebuffer *before* underrun, based on raw chunk-gap / chunk-duration ratio.
   */
  private sampleEarlyResponseGap(currentTime: number): void {
    if (this.earlyEvalDone) return;
    const prev = this.chunkArrivalTimes[this.chunkArrivalTimes.length - 2];
    if (prev === undefined) return; // Need at least 2 arrivals to measure a gap

    this.earlyResponseGaps.push(currentTime - prev);
    if (this.earlyResponseGaps.length >= this.EARLY_SAMPLE_COUNT) {
      this.evaluateNetworkHealth();
      this.earlyEvalDone = true;
    }
  }

  /**
   * Compare early chunk-arrival cadence against the expected chunk duration and bump the
   * prebuffer *immediately* if the connection is clearly too slow to keep up.
   *
   * OpenAI sends ~400ms PCM16 chunks. If they arrive faster than realtime
   * (~80ms apart, which packs ~5× speed), we're fine. If gaps approach or exceed the chunk
   * duration, the buffer is going to starve — so we preemptively grow it before the
   * reactive monitor would catch it.
   */
  private evaluateNetworkHealth(): void {
    // OpenAI sends ~400ms PCM16 chunks (verified via telemetry — chunkSizeAvgMs ≈ 395ms).
    // Earlier code assumed 20ms which made every connection look 20× too slow and triggered
    // the predictive buffer bump on every response.
    const expectedChunkDurationMs = 400;
    const avgGap = this.earlyResponseGaps.reduce((a, b) => a + b, 0) / this.earlyResponseGaps.length;
    const ratio = avgGap / expectedChunkDurationMs;

    let suggestedMs = this.prebufferTime * 1000;
    if (ratio > 1.5) {
      // Significantly slower than realtime — bump hard
      suggestedMs = Math.min(this.getPredictiveBufferCapMs(), suggestedMs + 800);
    } else if (ratio > 1.2) {
      // Mildly slow — moderate bump
      suggestedMs = Math.min(this.getPredictiveBufferCapMs(), suggestedMs + 400);
    }

    if (suggestedMs > this.prebufferTime * 1000) {
      logger.debug(`[🎯 PREDICTIVE] Slow network detected (ratio=${ratio.toFixed(2)}x) — buffer ${(this.prebufferTime * 1000).toFixed(0)}ms → ${suggestedMs.toFixed(0)}ms`);
      this.prebufferTime = suggestedMs / 1000;
      this.prebufferSamples = Math.floor(this.prebufferTime * 24000);
    }
  }

  /**
   * Commit the current response's prebuffer as learned state for the next response,
   * with a small decay on clean (no-starvation) responses so we can probe back down to
   * the browser default over time on improving networks.
   */
  private commitLearnedPrebuffer(): void {
    const currentMs = this.prebufferTime * 1000;
    const defaultMs = this.getDefaultPrebufferMs();
    // The audioBufferSource path doesn't queue chunks in JS land (they're scheduled into
    // AudioContext synchronously on arrival), so its `prebufferTime` doesn't reflect real
    // headroom. But scheduling underruns *do* indicate a flaky network, and we can use
    // them to seed the next response's starting prebuffer — which gates the *initial*
    // playback latency and feeds the predictive evaluator early in the next response.
    const hadUnderrunPressure = this.responseStats.scheduleUnderrunCount > 2;

    if (this.responseStats.starvationCount === 0 && !hadUnderrunPressure && currentMs > defaultMs) {
      // Clean response — decay toward default by 100ms
      this.learnedPrebufferMs = Math.max(defaultMs, currentMs - 100);
    } else if (hadUnderrunPressure) {
      // Underruns this response → next response should start with more headroom.
      // Bump by 200ms above current, capped by predictive cap.
      const bumped = Math.min(this.getPredictiveBufferCapMs(), Math.max(currentMs, defaultMs) + 200);
      this.learnedPrebufferMs = bumped;
    } else {
      // Had trouble — carry current value forward (capped)
      this.learnedPrebufferMs = Math.min(this.getPredictiveBufferCapMs(), currentMs);
    }
  }

  /**
   * Observe the opening window of the response to set a per-utterance gain.
   * No-op once the gain has been decided for the current response.
   *
   * The first ~500ms play at 1.0 (unaffected), then we ramp smoothly over 50ms
   * to the computed gain. Reset happens in softResetForNextResponse.
   */
  private observeForNormalization(samples: Float32Array): void {
    if (this.normalizationDecided || !this.normalizationGainNode) return;

    // Track absolute peak across the measurement window.
    let peak = this.normalizationPeak;
    for (let i = 0; i < samples.length; i++) {
      const abs = samples[i] < 0 ? -samples[i] : samples[i];
      if (abs > peak) peak = abs;
    }
    this.normalizationPeak = peak;
    this.normalizationSamplesObserved += samples.length;

    if (this.normalizationSamplesObserved < AudioPlayback.NORMALIZATION_WINDOW_SAMPLES) {
      return;
    }

    // Window complete — decide gain. If the opening was effectively silent
    // (likely a long lead-in pause), defer the decision rather than blowing it
    // up. We can't keep deferring forever though, so cap by samples observed.
    const observedSeconds = this.normalizationSamplesObserved / 24000;
    if (peak < AudioPlayback.NORMALIZATION_SILENCE_PEAK && observedSeconds < 1.5) {
      return;
    }

    let gain = peak > 0
      ? AudioPlayback.NORMALIZATION_TARGET_PEAK / peak
      : AudioPlayback.NORMALIZATION_MIN_GAIN;
    if (gain < AudioPlayback.NORMALIZATION_MIN_GAIN) gain = AudioPlayback.NORMALIZATION_MIN_GAIN;
    if (gain > AudioPlayback.NORMALIZATION_MAX_GAIN) gain = AudioPlayback.NORMALIZATION_MAX_GAIN;

    const now = this.audioContext.currentTime;
    try {
      this.normalizationGainNode.gain.cancelScheduledValues(now);
      this.normalizationGainNode.gain.setValueAtTime(this.normalizationGainNode.gain.value, now);
      this.normalizationGainNode.gain.linearRampToValueAtTime(gain, now + 0.05);
    } catch {
      this.normalizationGainNode.gain.value = gain;
    }

    this.normalizationDecided = true;
    logger.debug(
      `[🔊 NORM] response peak=${peak.toFixed(3)} → gain=${gain.toFixed(2)}x ` +
      `(observed=${observedSeconds.toFixed(2)}s)`
    );
  }

  /**
   * Start per-response stats tracking. Called when the first chunk of a new response arrives.
   */
  private beginResponseStats(): void {
    this.responseStats = {
      startedAt: performance.now(),
      startPrebufferMs: Math.round(this.prebufferTime * 1000),
      minBufferLevelSeen: 1.0,
      starvationCount: 0,
      jitterEverDetected: false,
      lateChunksAfterDone: 0,
      totalChunks: 0,
      scheduleUnderrunCount: 0,
      scheduleUnderrunMaxMs: 0,
      scheduleUnderrunTotalMs: 0,
      smallChunkCount: 0,
    };
    this.resetCallbackStats();
  }

  /**
   * Emit a structured one-line summary at the end of a response, to correlate
   * user-visible audio quality with internal buffer behavior.
   */
  private logResponseHealth(reason: 'complete' | 'reset'): void {
    if (this.responseStats.startedAt === 0) return;
    const durationMs = Math.round(performance.now() - this.responseStats.startedAt);
    const callbackP95Ms = this.snapshotCallbackP95Ms();
    const callbackAvgMs = this.callbackCount > 0 ? this.callbackSumMs / this.callbackCount : 0;
    logger.debug(
      `[🎯 HEALTH] ${reason} | duration=${durationMs}ms chunks=${this.responseStats.totalChunks} ` +
      `startBuffer=${this.responseStats.startPrebufferMs}ms endBuffer=${Math.round(this.prebufferTime * 1000)}ms ` +
      `minLevel=${(this.responseStats.minBufferLevelSeen * 100).toFixed(0)}% ` +
      `starvations=${this.responseStats.starvationCount} jitter=${this.responseStats.jitterEverDetected} ` +
      `lateAfterDone=${this.responseStats.lateChunksAfterDone} ` +
      `underruns=${this.responseStats.scheduleUnderrunCount} underrunMax=${this.responseStats.scheduleUnderrunMaxMs.toFixed(0)}ms ` +
      `smallChunks=${this.responseStats.smallChunkCount} ` +
      `cbCount=${this.callbackCount} cbAvg=${callbackAvgMs.toFixed(2)}ms cbP95=${callbackP95Ms.toFixed(2)}ms cbMax=${this.callbackMaxMs.toFixed(2)}ms`
    );

    // Emit a structured snapshot for server-side analysis. Fire-and-forget; if no writer
    // is registered (e.g. tests), this is a no-op.
    const SOURCE_SAMPLE_RATE = 24000;
    const interCallbackGapAvgMs = this.interCallbackGapCount > 0 ? this.interCallbackGapSumMs / this.interCallbackGapCount : 0;
    const interCallbackGapP95Ms = this.snapshotReservoirP95(this.interCallbackGapReservoir, this.interCallbackGapReservoirCount);
    const chunkSizeAvgSamples = this.chunkSizeCount > 0 ? this.chunkSizeSumSamples / this.chunkSizeCount : 0;
    const chunkSizeMinSamples = this.chunkSizeCount > 0 ? this.chunkSizeMinSamples : 0;
    const interChunkGapAvgMs = this.interChunkGapCount > 0 ? this.interChunkGapSumMs / this.interChunkGapCount : 0;
    const interChunkGapP95Ms = this.snapshotReservoirP95(this.interChunkGapReservoir, this.interChunkGapReservoirCount);
    const unconsumedSamples = this.useScriptProcessor
      ? this.totalBufferedSamples
      : this.streamingBuffer.reduce((acc, c) => acc + c.length, 0);

    this.recentResponseHealth.push({
      finishedAt: performance.now(),
      starvationCount: this.responseStats.starvationCount,
      scheduleUnderrunCount: this.responseStats.scheduleUnderrunCount,
      jitterEverDetected: this.responseStats.jitterEverDetected,
      endPrebufferMs: Math.round(this.prebufferTime * 1000),
      interChunkGapAvgMs: Number(interChunkGapAvgMs.toFixed(2)),
    });
    if (this.recentResponseHealth.length > 5) {
      this.recentResponseHealth.shift();
    }

    const ctx = getAudioTelemetryContext();
    recordAudioHealth({
      sessionId: ctx.sessionId,
      userId: ctx.userId,
      responseIndex: this.responseCount,
      durationMs,
      totalChunks: this.responseStats.totalChunks,
      lateChunksAfterDone: this.responseStats.lateChunksAfterDone,
      startPrebufferMs: this.responseStats.startPrebufferMs,
      endPrebufferMs: Math.round(this.prebufferTime * 1000),
      minBufferLevelPct: Math.round(this.responseStats.minBufferLevelSeen * 100),
      starvationCount: this.responseStats.starvationCount,
      jitterEverDetected: this.responseStats.jitterEverDetected,
      callbackCount: this.callbackCount,
      callbackSumMs: Number(this.callbackSumMs.toFixed(2)),
      callbackMaxMs: Number(this.callbackMaxMs.toFixed(2)),
      callbackP95Ms: Number(callbackP95Ms.toFixed(2)),
      callbackAvgMs: Number(callbackAvgMs.toFixed(2)),
      interCallbackGapAvgMs: Number(interCallbackGapAvgMs.toFixed(2)),
      interCallbackGapP95Ms: Number(interCallbackGapP95Ms.toFixed(2)),
      interCallbackGapMaxMs: Number(this.interCallbackGapMaxMs.toFixed(2)),
      totalAudioMs: Number(((this.chunkAudioSamplesTotal / SOURCE_SAMPLE_RATE) * 1000).toFixed(0)),
      chunkSizeAvgMs: Number(((chunkSizeAvgSamples / SOURCE_SAMPLE_RATE) * 1000).toFixed(2)),
      chunkSizeMinMs: Number(((chunkSizeMinSamples / SOURCE_SAMPLE_RATE) * 1000).toFixed(2)),
      chunkSizeMaxMs: Number(((this.chunkSizeMaxSamples / SOURCE_SAMPLE_RATE) * 1000).toFixed(2)),
      interChunkGapAvgMs: Number(interChunkGapAvgMs.toFixed(2)),
      interChunkGapP95Ms: Number(interChunkGapP95Ms.toFixed(2)),
      interChunkGapMaxMs: Number(this.interChunkGapMaxMs.toFixed(2)),
      scheduleUnderrunCount: this.responseStats.scheduleUnderrunCount,
      scheduleUnderrunMaxMs: Number(this.responseStats.scheduleUnderrunMaxMs.toFixed(2)),
      scheduleUnderrunTotalMs: Number(this.responseStats.scheduleUnderrunTotalMs.toFixed(2)),
      smallChunkCount: this.responseStats.smallChunkCount,
      endReason: reason,
      ringBufferLen: this.useScriptProcessor
        ? this.playbackRingBuffer.length - this.chunkReadIndex
        : this.streamingBuffer.length,
      unconsumedSamplesAtEnd: unconsumedSamples,
      userAgent: navigator.userAgent,
      isFirefox: this.isFirefox,
      isFirefoxMobile: this.isFirefoxMobile,
      isMobile: this.isMobile,
      audioContextSampleRate: this.audioContext.sampleRate,
      playbackPath: this.useScriptProcessor ? 'scriptProcessor' : 'audioBufferSource',
    });

    this.responseStats.startedAt = 0;
  }

  /**
   * Play PCM16 audio data with streaming buffer (for WebSocket chunks)
   * Accumulates chunks in buffer and plays smoothly without speed issues.
   * @param pcm16Data - PCM16 audio data (16-bit, 24kHz, mono, little-endian)
   */
  playPCM16DataStreaming(pcm16Data: Uint8Array): void {
    try {
      // Convert PCM16 to Float32
      const samples = new Float32Array(pcm16Data.length / 2);
      for (let i = 0; i < samples.length; i++) {
        const low = pcm16Data[i * 2];
        const high = pcm16Data[i * 2 + 1];
        const int16 = (high << 8) | low;
        const signed = int16 >= 0x8000 ? int16 - 0x10000 : int16;
        samples[i] = signed / 32767.0;
      }
      
      // ⏸️ If paused, store chunks instead of scheduling them
      if (this.isPaused) {
        if (this.useScriptProcessor) {
          this.pausedRingBuffer.push(samples);
          this.pausedTotalBufferedSamples += samples.length;
        } else {
          this.pausedChunks.push(samples);
          this.pausedTotalBufferedSamples += samples.length;
        }
        return; // Don't schedule while paused
      }

      // 🔊 Per-utterance peak normalization: observe opening window, then ramp
      // normalizationGainNode to a target headroom. masterGainNode is already
      // ensured via ensureMasterGainNode() below the playback split, but we
      // need the analysis to happen now (single entry point for both paths).
      this.ensureMasterGainNode();
      this.observeForNormalization(samples);

      // 🎯 Adaptive jitter detection and prebuffer adjustment
      const currentTime = performance.now();
      this.detectAndAdaptToJitter(currentTime);

      // 🎯 Predictive: evaluate early arrival gaps to preempt starvation
      this.sampleEarlyResponseGap(currentTime);

      // 🎯 Per-response health tracking
      if (this.responseStats.startedAt === 0) {
        this.beginResponseStats();
      }
      this.responseStats.totalChunks++;
      if (this.responseFinished) {
        this.responseStats.lateChunksAfterDone++;
      }
      if (this.jitterDetected) {
        this.responseStats.jitterEverDetected = true;
      }
      // Count "small" chunks (<200ms) at arrival, not at schedule. The previous site was
      // inside scheduleNextChunk which only runs on the audioBufferSource path AND only when
      // we're actively draining — chunks that arrive after audio.done were never counted.
      // This site fires for every chunk on both playback paths.
      const chunkMs = (samples.length / 24000) * 1000;
      if (chunkMs < 200) {
        this.responseStats.smallChunkCount++;
      }
      // Telemetry: chunk-size and inter-chunk gap distributions for this response.
      this.recordChunkArrival(samples.length, currentTime);

      // CRITICAL FIX: Ensure AudioContext is running (mobile Safari requirement)
      if (this.audioContext.state === 'suspended') {
        console.warn('[🔊 STREAM] AudioContext suspended - resuming...');
        this.audioContext.resume().catch(err => {
          console.error('[🔊 STREAM] Failed to resume AudioContext:', err);
        });
      }

      // Log AudioContext state once (debug mobile issues)
      if (!this.sampleRateLogged) {
        logger.debug(`[🔊 STREAM] AudioContext: state=${this.audioContext.state}, sampleRate=${this.audioContext.sampleRate}Hz`);
        this.sampleRateLogged = true;
      }

      // Calculate prebuffer threshold
      const SOURCE_SAMPLE_RATE = 24000;  // OpenAI sends 24kHz
      this.prebufferSamples = Math.floor(this.prebufferTime * SOURCE_SAMPLE_RATE);
      
      // 📱 ScriptProcessor (Mobile + Firefox) vs 💻 AudioBufferSourceNode (Chrome Desktop)
      if (this.useScriptProcessor) {
        // 📱 ScriptProcessor: Use continuous callback (no manual scheduling)
        // Add Float32 samples to ring buffer (no accumulation needed)
        this.playbackRingBuffer.push(samples);
        this.totalBufferedSamples += samples.length;
        this.totalSamplesReceived += samples.length;
        
        // Start ScriptProcessorNode playback when enough buffered
        if (!this.playbackStarted && this.totalBufferedSamples >= this.prebufferSamples) {
          logger.debug(`[📱 MOBILE] Starting playback: buffered=${this.totalBufferedSamples} samples`);
          this.startScriptProcessorPlayback();
          // Enable reactive buffer growth + starvation tracking on Firefox/mobile too.
          // Previously only Chrome Desktop ran this monitor, which left
          // commitLearnedPrebuffer() wrongly classifying every Firefox mobile response
          // as "clean" (starvationCount always 0) and decaying the learned buffer.
          this.startBufferHealthMonitoring();
        }
        // Note: Once started, ScriptProcessor keeps running until resetStreamingBuffer()
        // This allows it to play chunks that arrive after buffer temporarily empties
      } else {
        // 💻 Chrome Desktop: Use AudioBufferSourceNode (manual scheduling)
        this.streamingBuffer.push(samples);
        this.totalBufferedSamples += samples.length;
        this.totalSamplesReceived += samples.length;
        
        // Start playback when we have enough buffered
        if (!this.playbackStarted && this.totalBufferedSamples >= this.prebufferSamples) {
          this.responseCount++;
          this.playbackStarted = true;
          
          // 🎵 Create analyser node for waveform visualization
          if (!this.playbackAnalyserNode) {
            this.playbackAnalyserNode = this.audioContext.createAnalyser();
            this.playbackAnalyserNode.fftSize = 512;
            this.playbackAnalyserNode.smoothingTimeConstant = 0.3;
            this.playbackAnalyserNode.minDecibels = -90;
            this.playbackAnalyserNode.maxDecibels = -10;
          }
          
          // 🎯 Start adaptive buffer monitoring
          this.startBufferHealthMonitoring();
          
          // 🔧 Calculate fresh start time
          const now = this.audioContext.currentTime;
          this.nextScheduledTime = now + this.minScheduleAhead;
          
          this.scheduleNextChunk();
        } else if (this.playbackStarted || this.responseFinished) {
          // Continue scheduling chunks (even after response.audio.done for late arrivals)
          this.scheduleNextChunk();
        }
      }
      
    } catch (error) {
      console.error('[🔊 STREAM] Error in streaming playback:', error);
      this.events.onError?.(error as Error);
    }
  }

  /**
   * Schedule next audio chunk for playback
   */
  private scheduleNextChunk(): void {
    // 🔧 SAFETY: If nextScheduledTime is invalid, recalculate
    if (this.nextScheduledTime < 0.001) {
      const now = this.audioContext.currentTime;
      this.nextScheduledTime = now + this.minScheduleAhead;
    }
    
    // Schedule all available chunks
    while (this.streamingBuffer.length > 0) {
      const chunk = this.streamingBuffer.shift();
      if (!chunk) break;

      this.totalBufferedSamples -= chunk.length;

      // 🎮 RENT gain from pool (GainNodes can be pooled, AudioBuffers cannot due to size variance)
      const SOURCE_SAMPLE_RATE = 24000;

      // Create AudioBuffer with EXACT size (cannot pool - sizes vary per chunk)
      const audioBuffer = this.audioContext.createBuffer(1, chunk.length, SOURCE_SAMPLE_RATE);

      // 🎯 CRITICAL FIX: Zero-fill entire buffer to prevent memory garbage clicks
      // This eliminates audio artifacts from uninitialized memory
      const channelData = audioBuffer.getChannelData(0);

      // 🦊 FIX: Always zero-fill buffer before copying data (prevents garbage)
      // Note: Synchronous only - async setTimeout causes "detached ArrayBuffer" errors
      channelData.fill(0);

      channelData.set(chunk); // Then copy actual audio data
      
      // Create BufferSource (cannot pool - Web Audio API limitation)
      const bufferSource = this.audioContext.createBufferSource();
      bufferSource.buffer = audioBuffer;
      
      // 🎮 RENT GainNode from pool (THIS can be safely pooled!)
      // NOTE: Per-chunk gain is 1.0 (pass-through) - amplification is handled by masterGainNode.
      // The gain may be re-armed below with a short fade-in if we detect a scheduling underrun
      // (network gap that left nextScheduledTime in the past).
      const pooledGain = this.nodePool.rentGain();
      const gainNode = pooledGain || this.audioContext.createGain();
      gainNode.gain.value = 1.0; // Amplification applied via masterGainNode, not per-chunk
      
      // Track if gain came from pool (for return later)
      const pooledNodes: PooledAudioNodes = {
        buffer: audioBuffer,
        source: bufferSource,
        gain: gainNode,
        fromPool: !!pooledGain
      };
      
      // Track active pooled nodes for cleanup
      this.activePooledNodes.set(bufferSource, pooledNodes);
      
      // 🎵 CRITICAL FIX: Connect through analyser for waveform visualization
      // Connect: BufferSource → Gain → Master Gain → Analyser → Destination
      
      // Use centralized method to ensure consistent masterGainNode initialization
      this.ensureMasterGainNode();
      
      // Connect per-chunk nodes (source → gain → masterGain)
      // Master chain already connected by ensureMasterGainNode
      pooledNodes.source.connect(pooledNodes.gain);
      pooledNodes.gain.connect(this.masterGainNode!);
      
      // 🔧 SAFETY CHECK: Never schedule in the past or too close to now
      const now = this.audioContext.currentTime;
      let minSafeTime = now + 0.01; // At least 10ms in future

      if (this.isFirefoxMobile) {
        // 🦊 FIREFOX MOBILE: More conservative timing to prevent clicks
        minSafeTime = now + 0.05; // 50ms minimum (5x more conservative)
      } else if (this.isFirefox) {
        // 🦊 FIX #3: FIREFOX DESKTOP: More conservative than Chrome
        minSafeTime = now + 0.03; // 30ms minimum (3x more conservative)
      }

      // Scheduling underrun: nextScheduledTime fell behind audioContext.currentTime,
      // i.e. the previous chunk has already finished playing and there is a real silent
      // gap. This is the signal we previously had no metric for — telemetry tracked
      // network gaps but not the resulting playback gap.
      const underrunMs = Math.max(0, (now - this.nextScheduledTime) * 1000);

      if (this.nextScheduledTime < minSafeTime) {
        if (underrunMs > 0) {
          this.responseStats.scheduleUnderrunCount++;
          this.responseStats.scheduleUnderrunTotalMs += underrunMs;
          if (underrunMs > this.responseStats.scheduleUnderrunMaxMs) {
            this.responseStats.scheduleUnderrunMaxMs = underrunMs;
          }
        }
        if (this.isFirefox) {
          logger.debug(`[🦊 FIREFOX] Scheduling behind by ${(minSafeTime - this.nextScheduledTime).toFixed(3)}s, jumping forward`);
        }
        this.nextScheduledTime = minSafeTime;
      }

      // Mask the boundary click on real underruns with an 8ms fade-in on the new chunk.
      // Same shape as the master-gain transition pattern used elsewhere in this file.
      // Gated on >5ms so normal first-chunk warm-up and tiny rounding cases stay byte-identical.
      if (underrunMs > 5) {
        gainNode.gain.cancelScheduledValues(this.nextScheduledTime);
        gainNode.gain.setValueAtTime(0, this.nextScheduledTime);
        gainNode.gain.linearRampToValueAtTime(1.0, this.nextScheduledTime + 0.008);
      }

      // Schedule playback at current time
      pooledNodes.source.start(this.nextScheduledTime);
      
      // Calculate when this chunk will finish
      const chunkDuration = chunk.length / SOURCE_SAMPLE_RATE;
      this.nextScheduledTime += chunkDuration;
      // 🎯 Text-audio sync: Record the AudioContext time at which playback actually
      // started (the first scheduled chunk's start time). getElapsedPlaybackMs()
      // computes real elapsed from (audioContext.currentTime - playbackStartContextTime),
      // so the karaoke text tracks what the user HEARS, not what has been scheduled
      // ahead. Previously we accumulated chunkDuration at schedule time, which raced
      // ahead of real playback on fast networks and revealed all text at once.
      if (this.playbackStartContextTime === 0) {
        this.playbackStartContextTime = this.nextScheduledTime - chunkDuration;
      }
      // 🎯 Text-audio sync: Capture the response generation so onended can detect
      // if a new response has started (generation changed) and skip onPlaybackStop.
      const scheduledGeneration = this.responseGeneration;
      
      // 🎮 Clean up when done AND return gain to pool (if it came from pool)
      pooledNodes.source.onended = () => {
        // Disconnect nodes
        pooledNodes.source.disconnect();
        pooledNodes.gain.disconnect();
        
        // 🎮 RETURN gain to pool for reuse (if it came from pool)
        if (pooledNodes.fromPool) {
          this.nodePool.returnGain(pooledNodes.gain);
        }
        this.activePooledNodes.delete(pooledNodes.source);
        
        // Remove from scheduled nodes
        const index = this.scheduledNodes.indexOf(pooledNodes.source);
        if (index > -1) {
          this.scheduledNodes.splice(index, 1);
        }
        
        // If no more scheduled and buffer empty, playback MIGHT be done.
        // CRITICAL: Only fire onPlaybackStop if:
        // 1. responseFinished is true (audio.done received) — prevents premature
        //    stop on transient network gaps between chunks.
        // 2. responseGeneration hasn't changed — prevents the previous response's
        //    responseFinished=true from leaking into the new response (the
        //    "sem itálico" bug from the second message onward).
        if (this.scheduledNodes.length === 0 && this.streamingBuffer.length === 0) {
          if (!this.responseFinished) {
            // More chunks may arrive — don't stop, just wait.
            logger.debug('[🔊 STREAM] All scheduled nodes done but response not finished — waiting for more chunks');
          } else if (scheduledGeneration !== this.responseGeneration) {
            // This onended belongs to the previous response — a new response has
            // started. Don't fire onPlaybackStop; the new response will handle it.
            logger.debug('[🔊 STREAM] onended for previous response generation — ignoring (new response started)');
          } else {
            this.playbackStarted = false;
            this.isPlaying = false;
            // 🎯 Text-audio sync: Freeze the real elapsed value at stop time so
            // getElapsedPlaybackMs() returns a stable value (not 0) until the next
            // response resets it. This keeps the karaoke text frozen at its final
            // position during the brief window before aiFinishing finalizes.
            this.freezeRealPlayedMs();
            this.events.onPlaybackStop?.();

            // 🎯 Stop adaptive buffer monitoring
            this.stopBufferHealthMonitoring();

            // 🎯 Emit per-response health summary
            this.logResponseHealth('complete');

            // 🎮 Log pool stats on playback complete
            const stats = this.nodePool.getStats();
            logger.debug('[🔊 STREAM] Playback complete - Pool stats:', stats);
          }
        }
      };
      
      // Track scheduled node
      this.scheduledNodes.push(pooledNodes.source);
      
      // Trigger playback event on first chunk
      if (!this.isPlaying) {
        this.isPlaying = true;
        this.events.onPlaybackStart?.();
      }
    }
  }

  /**
   * Reset streaming buffer (call when starting new response)
   */
  /**
   * 📱 Finish current streaming response (flush remaining chunks)
   * Call this when OpenAI signals audio response is complete
   */
  finishStreamingResponse(): void {
    logger.debug('[🔧 FINISH] Response audio generation complete (response.audio.done received)');
    
    // 🎯 CRITICAL: Mark response as finished
    // This allows late-arriving chunks (still in network transit) to be scheduled
    // when they arrive via playPCM16DataStreaming()
    this.responseFinished = true;
    
    // 🎯 CRITICAL FIX: Schedule ALL chunks currently in buffer
    // When response.audio.done arrives, there may already be chunks in the buffer
    // that haven't been scheduled yet. Schedule them NOW.
    // (Only for AudioBufferSourceNode path - ScriptProcessor drains buffer continuously)
    if (!this.useScriptProcessor && this.streamingBuffer.length > 0) {
      logger.debug(`[🔧 FINISH] 🎵 Scheduling ${this.streamingBuffer.length} buffered chunks immediately`);
      this.scheduleNextChunk();
    }
    
    // 📝 NOTE: We do NOT use timeouts here. Chunks that arrive after this point
    // will be scheduled normally by playPCM16DataStreaming() because the condition
    // "playbackStarted || responseFinished" will be true.
    // This handles chunks with ANY network delay, not just artificial 200ms.
    
    // 📱 ScriptProcessor (Mobile + Firefox): Ring buffer will drain naturally via onaudioprocess
    // (ScriptProcessor continuously drains the buffer, no special handling needed)
  }

  /**
   * Return the per-browser default prebuffer, in ms.
   * Centralizes what was previously duplicated in the constructor and resetStreamingBuffer.
   */
  private getDefaultPrebufferMs(): number {
    // Mobile defaults are tight on purpose: the browser's AEC decalibrates as
    // end-to-end playback delay grows, letting the avatar's voice leak into the
    // mic. Firefox Mobile is most sensitive (AEC fails above ~600-800ms);
    // Chrome Mobile / iOS Safari hold up better but still benefit from lower
    // baseline buffers — saw 3000ms-pinned buffers and click artefacts at that
    // size on Chrome Android.
    if (this.isFirefoxMobile) return 600;
    if (this.isFirefox) return 1000;
    if (this.isMobile) return 800;
    return 800;
  }

  /**
   * Stop the per-response processor/source nodes and clear their buffers,
   * without disconnecting the master audio chain (gain/compressor/analyser).
   *
   * Why: previously the only reset path tore down the whole graph between responses,
   * which is audible as a click in Firefox (and costs an extra setup on every response).
   * The master chain is long-lived; per-response nodes are the short-lived ones.
   */
  softResetForNextResponse(): void {
    // Capture whatever the just-ended response learned before we discard its state
    this.commitLearnedPrebuffer();

    // Emit per-response health summary before clearing state
    this.logResponseHealth('reset');

    this.stopBufferHealthMonitoring();

    // Clear per-response buffers. The ScriptProcessor itself is KEPT ALIVE across responses
    // (see startScriptProcessorPlayback / markScriptProcessorIdle) to avoid Firefox mobile
    // GC churn. The next response will call startScriptProcessorPlayback which no-ops if the
    // processor is already there.
    if (this.useScriptProcessor) {
      this.playbackRingBuffer = [];
      this.playbackBufferReadIndex = 0;
      this.chunkReadIndex = 0;
      this.playbackStarted = false;
      this.isPlaying = false;
      this.silenceCounter = 0;
    }

    this.scheduledNodes.forEach(node => {
      try {
        node.stop();
        node.disconnect();
        const pooledNodes = this.activePooledNodes.get(node);
        if (pooledNodes) {
          pooledNodes.gain.disconnect();
          if (pooledNodes.fromPool) {
            this.nodePool.returnGain(pooledNodes.gain);
          }
          this.activePooledNodes.delete(node);
        }
      } catch {
        // Ignore errors from already-stopped nodes
      }
    });

    this.scheduledNodes = [];
    this.streamingBuffer = [];
    this.totalBufferedSamples = 0;
    this.playbackStarted = false;
    this.responseFinished = false;
    this.isPlaying = false;
    this.nextScheduledTime = 0;
    this.silenceCounter = 0;
    // 🎯 Text-audio sync: Reset per-response tracking
    this.accumulatedPlayedMs = 0;
    this.totalSamplesReceived = 0;
    this.playbackStartContextTime = 0;
    this.accumulatedRealPlayedMs = 0;
    // 🎯 Text-audio sync: Increment generation so onended callbacks from the
    // previous response know not to fire onPlaybackStop (new response started).
    this.responseGeneration++;

    // Start from learned value when available (so response N+1 doesn't re-pay the
    // starvation cost that response N already paid). Always floored at the per-browser
    // default and capped by the predictive cap.
    const defaultMs = this.getDefaultPrebufferMs();
    const startMs = Math.max(defaultMs, Math.min(this.getPredictiveBufferCapMs(), this.learnedPrebufferMs || defaultMs));
    this.prebufferTime = startMs / 1000;
    this.prebufferSamples = Math.floor(this.prebufferTime * 24000);
    this.minBufferLevelSeen = 1.0;
    this.bufferStarvationCount = 0;
    this.timeInHealthyZone = 0;

    this.chunkArrivalTimes = [];
    this.averageInterChunkLatency = 0;
    this.jitterDetected = false;

    // Reset predictive early-sample state for the next response
    this.earlyResponseGaps = [];
    this.earlyEvalDone = false;

    // Reset per-utterance normalization. Ramp gain back to 1.0 over 30ms so the
    // next response starts neutral; observeForNormalization will re-decide it.
    this.normalizationPeak = 0;
    this.normalizationSamplesObserved = 0;
    this.normalizationDecided = false;
    if (this.normalizationGainNode) {
      const now = this.audioContext.currentTime;
      try {
        this.normalizationGainNode.gain.cancelScheduledValues(now);
        this.normalizationGainNode.gain.setValueAtTime(this.normalizationGainNode.gain.value, now);
        this.normalizationGainNode.gain.linearRampToValueAtTime(1.0, now + 0.03);
      } catch {
        this.normalizationGainNode.gain.value = 1.0;
      }
    }

    logger.debug(`[🔧 SOFT-RESET] Prebuffer ${startMs}ms (learned=${this.learnedPrebufferMs || 'n/a'}, default=${defaultMs}) — master chain preserved`);
  }

  /**
   * Full reset: soft-resets per-response state AND tears down the master chain.
   * Use this on teardown, error, or hard interrupts (user cancels / VAD interrupt),
   * not between consecutive responses — see softResetForNextResponse().
   */
  resetStreamingBuffer(): void {
    this.softResetForNextResponse();

    // Full teardown also destroys the session-long ScriptProcessorNode
    if (this.useScriptProcessor && this.playbackProcessor) {
      this.stopScriptProcessorPlayback();
    }

    if (this.masterGainNode) {
      try {
        this.masterGainNode.disconnect();
        logger.debug('[🔊 RESET] Master gain node disconnected');
      } catch {
        // Already disconnected
      }
      this.masterGainNode = null;
    }

    if (this.compressorNode) {
      try {
        this.compressorNode.disconnect();
      } catch {
        // Already disconnected
      }
      this.compressorNode = null;
    }

    if (this.normalizationGainNode) {
      try {
        this.normalizationGainNode.disconnect();
      } catch {
        // Already disconnected
      }
      this.normalizationGainNode = null;
    }

    if (this.playbackAnalyserNode) {
      try {
        this.playbackAnalyserNode.disconnect();
        logger.debug('[🔊 RESET] Playback analyser node disconnected');
      } catch {
        // Already disconnected
      }
      this.playbackAnalyserNode = null;
    }
  }

  /**
   * Play PCM16 audio data (Legacy - single chunk playback)
   * @param pcm16Data - PCM16 audio data (16-bit, 24kHz, mono, little-endian)
   * @param id - Unique identifier for this audio stream
   */
  playPCM16Data(pcm16Data: Uint8Array, _id = 'default'): void {
    try {
      // Drop audio data if paused
      if (this.isPaused) {
        return;
      }

      // Convert PCM16 to Float32
      const samples = new Float32Array(pcm16Data.length / 2);
      for (let i = 0; i < samples.length; i++) {
        const low = pcm16Data[i * 2];
        const high = pcm16Data[i * 2 + 1];
        const int16 = (high << 8) | low;
        const signed = int16 >= 0x8000 ? int16 - 0x10000 : int16;
        samples[i] = signed / 32767.0;
      }
      
      // Debug only once
      if (!this.conversionLogged) {
        this.conversionLogged = true;
        const maxValue = Math.max(...Array.from(samples).map(Math.abs));
        logger.debug('[🎙️ AEC] 🔍 PCM16 Conversion Check:', {
          totalSamples: samples.length,
          maxAmplitude: maxValue.toFixed(4),
          hasAudio: maxValue > 0.001,
          chunkDuration: (samples.length / 24000 * 1000).toFixed(1) + 'ms'
        });
      }
      
      // Create AudioBuffer with CORRECT sample rate (24kHz from OpenAI)
      // The AudioContext will automatically resample to its native rate
      const SOURCE_SAMPLE_RATE = 24000;
      const audioBuffer = this.audioContext.createBuffer(1, samples.length, SOURCE_SAMPLE_RATE);

      // 🎯 CRITICAL FIX: Zero-fill entire buffer to prevent memory garbage clicks
      // This eliminates audio artifacts from uninitialized memory
      const channelData = audioBuffer.getChannelData(0);

      // 🦊 FIX: Always zero-fill buffer before copying data (prevents garbage)
      // Note: Synchronous only - async setTimeout causes "detached ArrayBuffer" errors
      channelData.fill(0);

      channelData.set(samples); // Then copy actual audio data
      
      // Create BufferSource
      const bufferSource = this.audioContext.createBufferSource();
      bufferSource.buffer = audioBuffer;
      
      // Log sample rate info only once
      if (!this.sampleRateLogged) {
        this.sampleRateLogged = true;
        const contextSampleRate = this.audioContext.sampleRate;
        logger.debug('[🎙️ AEC] 🎵 Audio direct playback:', {
          source: SOURCE_SAMPLE_RATE + 'Hz (OpenAI)',
          context: contextSampleRate + 'Hz',
          samples: samples.length,
          duration: (samples.length / SOURCE_SAMPLE_RATE * 1000).toFixed(1) + 'ms',
          note: contextSampleRate !== SOURCE_SAMPLE_RATE ? 'Browser will resample automatically' : 'No resampling needed'
        });
      }
      
      // Create gain node for volume control
      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = this.config.volume ?? 1.0;
      
      // Connect: BufferSource → Gain → Destination
      bufferSource.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      // Start playback immediately
      bufferSource.start();
      
      // Clean up when done
      bufferSource.onended = () => {
        bufferSource.disconnect();
        gainNode.disconnect();
      };
      
      // Log play only occasionally
      if (!this.playStartLogged) {
        this.playStartLogged = true;
        logger.debug('[🎙️ AEC] 🔊 Playing PCM16 data (Unity-style) → destination');
        logger.debug('[🎙️ AEC] ✅ Browser sees this as "locally generated" audio - AEC will work!');
      }
      
      // Trigger playback event
      this.isPlaying = true;
      this.events.onPlaybackStart?.();
      
    } catch (error) {
      console.error('[🎙️ AEC] ❌ Error playing PCM16 data:', error);
      this.events.onError?.(error as Error);
    }
  }

  /**
   * Play audio from MediaStream (for WebRTC incoming audio)
   * @param stream - MediaStream from WebRTC
   * @param id - Unique identifier for this stream
   */
  playAudioStream(stream: MediaStream, id = 'default'): void {
    try {
      // Don't start new streams if paused
      if (this.isPaused) {
        logger.debug('[🔊 PAUSE] 🛑 Dropping MediaStream during pause');
        return;
      }

      logger.debug('[🔴 CHROME] 📥 playAudioStream called with stream:', {
        id: stream.id,
        active: stream.active,
        audioTracks: stream.getAudioTracks().length
      });
      
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        console.error('[🔴 CHROME] ❌ No audio tracks in stream!');
        return;
      }
      
      // Detailed track inspection
      audioTracks.forEach((track, index) => {
        logger.debug(`[🔴 CHROME] 🎵 Track ${index}:`, {
          id: track.id,
          kind: track.kind,
          label: track.label,
          enabled: track.enabled,
          muted: track.muted,
          readyState: track.readyState,
          settings: track.getSettings()
        });
        
        if (track.muted) {
          console.warn('[🔴 CHROME] ⚠️ Track is MUTED!');
          track.enabled = true;
        }
      });
      
      // Resume AudioContext if suspended
      if (this.audioContext.state === 'suspended') {
        logger.debug('[🔴 CHROME] ⚠️ AudioContext is suspended, attempting resume...');
        this.audioContext.resume().then(() => {
          logger.debug('[🔴 CHROME] ✅ AudioContext resumed in playAudioStream');
        });
      }

      // Clean up existing stream if any
      this.stopAudioStream(id);

      // Create HTML audio element as fallback for Chrome
      const audioElement = document.createElement('audio');
      audioElement.srcObject = stream;
      audioElement.autoplay = true;
      audioElement.volume = Math.min(1.0, this.config.volume ?? 1.0); // HTML audio volume clamped to 0-1
      
      // Start monitoring
      this.startPlaybackMonitoring();
      
      // Try to play
      const playPromise = audioElement.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            logger.debug('[🔴 CHROME] ✅ HTML <audio> element playing successfully!');
          })
          .catch((error) => {
            console.error('[🔴 CHROME] ❌ HTML <audio> element play failed:', error);
          });
      }
      
      this.playbackAudioElements.set(id, audioElement);

      // Create source from incoming MediaStream
      const sourceNode = this.audioContext.createMediaStreamSource(stream);
      
      // Create analyser for waveform visualization
      const analyserNode = this.audioContext.createAnalyser();
      analyserNode.fftSize = 512;
      analyserNode.smoothingTimeConstant = 0.3;
      analyserNode.minDecibels = -90;
      analyserNode.maxDecibels = -10;
      
      this.playbackAnalyserNode = analyserNode;
      
      // Use ScriptProcessor for pass-through
      const bufferSize = 2048;
      const scriptProcessor = this.audioContext.createScriptProcessor(bufferSize, 1, 1);
      
      scriptProcessor.onaudioprocess = (e) => {
        const inputData = e.inputBuffer.getChannelData(0);
        const outputData = e.outputBuffer.getChannelData(0);
        
        for (let i = 0; i < inputData.length; i++) {
          outputData[i] = inputData[i];
        }
      };
      
      // Silent GainNode for visualization without duplicate audio
      const silentGainNode = this.audioContext.createGain();
      silentGainNode.gain.value = 0; // Silent output
      
      sourceNode.connect(analyserNode);
      analyserNode.connect(scriptProcessor);
      scriptProcessor.connect(silentGainNode);
      silentGainNode.connect(this.audioContext.destination);
      
      logger.debug('[🔴 CHROME] 🎵 Web Audio API: source → analyser → scriptProcessor → SILENT gain → destination');
      logger.debug('[🔴 CHROME] 📺 HTML <audio> element handles actual playback');
      
      // Store nodes for cleanup
      this.playbackSources.set(id, sourceNode);
      this.playbackProcessors.set(id, scriptProcessor);
      this.playbackAnalysers.set(id, analyserNode);
      this.playbackGains.set(id, silentGainNode);
      
      // Trigger events
      this.isPlaying = true;
      this.events.onPlaybackStart?.();
      
    } catch (error) {
      console.error('❌ Error playing audio stream:', error);
      this.events.onError?.(error as Error);
    }
  }

  /**
   * Stop playing audio stream
   */
  stopAudioStream(id: string): void {
    try {
      // Cleanup HTML audio element
      const audioElement = this.playbackAudioElements.get(id);
      if (audioElement) {
        const stream = audioElement.srcObject as MediaStream;
        if (stream) {
          stream.getTracks().forEach(track => {
            track.stop();
          });
        }
        
        audioElement.pause();
        audioElement.srcObject = null;
        audioElement.src = '';
        this.playbackAudioElements.delete(id);
      }
      
      // Disconnect and cleanup source node
      const sourceNode = this.playbackSources.get(id);
      if (sourceNode) {
        const stream = (sourceNode as any).mediaStream;
        if (stream && stream.getTracks) {
          stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
        }
        sourceNode.disconnect();
        this.playbackSources.delete(id);
      }
      
      // Cleanup script processor
      const scriptProcessor = this.playbackProcessors.get(id);
      if (scriptProcessor) {
        scriptProcessor.disconnect();
        scriptProcessor.onaudioprocess = null;
        this.playbackProcessors.delete(id);
      }
      
      // Cleanup gain node
      const gainNode = this.playbackGains.get(id);
      if (gainNode) {
        gainNode.disconnect();
        this.playbackGains.delete(id);
      }
      
      // Cleanup analyser node
      const analyserNode = this.playbackAnalysers.get(id);
      if (analyserNode) {
        analyserNode.disconnect();
        this.playbackAnalysers.delete(id);
        
        if (this.playbackAnalyserNode === analyserNode) {
          this.playbackAnalyserNode = null;
        }
      }
      
      // Update state if no more streams playing
      if (this.playbackSources.size === 0) {
        this.isPlaying = false;
        this.events.onPlaybackStop?.();
      }
      
    } catch (error) {
      console.error('Error stopping audio stream:', error);
    }
  }

  /**
   * Stop ALL active playback streams immediately
   */
  stopAllPlayback(): void {
    try {
      logger.debug('[🎯 INTERRUPT] [stopAllPlayback] Starting...');
      
      // Stop all HTML audio elements and their tracks
      this.playbackAudioElements.forEach((audioElement) => {
        const stream = audioElement.srcObject as MediaStream;
        if (stream) {
          stream.getTracks().forEach(track => {
            track.stop();
          });
        }
        audioElement.pause();
        audioElement.srcObject = null;
        audioElement.src = '';
      });
      this.playbackAudioElements.clear();
      
      // Stop all Web Audio API nodes
      const playbackIds = Array.from(this.playbackSources.keys());
      playbackIds.forEach(id => {
        if (!id.endsWith('_extractor')) {
          this.stopAudioStream(id);
        }
      });
      
      // Force stop state
      this.isPlaying = false;
      // 🎯 Text-audio sync: Reset per-response tracking
      this.accumulatedPlayedMs = 0;
      this.totalSamplesReceived = 0;
      this.playbackStartContextTime = 0;
      this.accumulatedRealPlayedMs = 0;
      this.events.onPlaybackStop?.();
      
      logger.debug(`[🎯 INTERRUPT] [stopAllPlayback] ✅ Complete`);
    } catch (error) {
      console.error('[🎯 INTERRUPT] [stopAllPlayback] ❌ Error:', error);
    }
  }

  /**
   * Get waveform data for visualization
   * Returns array of 9 values (0-255) representing audio amplitude
   */
  getPlaybackFrequencyData(): Uint8Array {
    if (!this.playbackAnalyserNode) {
      return new Uint8Array(9).fill(0);
    }
    
    return this.getFrequencyDataFromAnalyser(this.playbackAnalyserNode);
  }

  /**
   * Check if audio is actually coming out of the playback analyser right now.
   * Uses the same time-domain data as the waveform visualization.
   */
  isPlaybackAudible(): boolean {
    if (!this.playbackAnalyserNode) return false;
    const buf = new Uint8Array(this.playbackAnalyserNode.fftSize);
    this.playbackAnalyserNode.getByteTimeDomainData(buf);
    for (let i = 0; i < buf.length; i++) {
      if (Math.abs(buf[i] - 128) > 2) return true;
    }
    return false;
  }

  /**
   * Internal helper to get frequency data from analyser node
   */
  private getFrequencyDataFromAnalyser(analyser: AnalyserNode): Uint8Array {
    const bufferLength = analyser.fftSize;
    const dataArray = new Uint8Array(bufferLength);
    
    // Get time domain data (waveform amplitude)
    analyser.getByteTimeDomainData(dataArray);
    
    // Group into 9 bars
    const barCount = 9;
    const barsData = new Uint8Array(barCount);
    const samplesPerBar = Math.floor(bufferLength / barCount);
    
    for (let i = 0; i < barCount; i++) {
      let maxValue = 0;
      const start = i * samplesPerBar;
      const end = start + samplesPerBar;
      
      for (let j = start; j < end && j < bufferLength; j++) {
        const amplitude = Math.abs(dataArray[j] - 128);
        maxValue = Math.max(maxValue, amplitude);
      }
      
      const amplified = maxValue * 1.5;
      barsData[i] = Math.min(255, amplified);
    }
    
    return barsData;
  }

  /**
   * Start monitoring if audio is actually playing
   */
  private startPlaybackMonitoring(): void {
    if (this.playbackMonitorInterval) {
      return;
    }

    let silenceCounter = 0;
    const SILENCE_THRESHOLD = 3;

    this.playbackMonitorInterval = setInterval(() => {
      if (!this.playbackAnalyserNode) {
        return;
      }

      const bufferLength = this.playbackAnalyserNode.fftSize;
      const dataArray = new Uint8Array(bufferLength);
      this.playbackAnalyserNode.getByteTimeDomainData(dataArray);

      const hasAudio = dataArray.some(value => Math.abs(value - 128) > 2);

      if (hasAudio) {
        silenceCounter = 0;
        if (!this.isPlaying) {
          this.isPlaying = true;
          this.events.onPlaybackStart?.();
        }
      } else {
        silenceCounter++;
        if (silenceCounter >= SILENCE_THRESHOLD && this.isPlaying) {
          this.isPlaying = false;
          this.events.onPlaybackStop?.();
        }
      }
    }, 100);
  }

  /**
   * Stop playback monitoring
   */
  private stopPlaybackMonitoring(): void {
    if (this.playbackMonitorInterval) {
      clearInterval(this.playbackMonitorInterval);
      this.playbackMonitorInterval = null;
    }
  }

  /**
   * Set volume for all playback streams
   */
  setVolume(volume: number): void {
    // Allow volume > 1.0 for amplification (useful for quiet audio sources like OpenAI TTS)
    const clampedVolume = Math.max(0, Math.min(3, volume)); // Max 3x amplification
    this.config.volume = clampedVolume;

    // Always save volume for later use (sample-level amplification, node creation, resume)
    this.savedVolume = clampedVolume;

    // Update master gain node if it exists and not paused
    // NOTE: For desktop path, masterGainNode applies the amplification.
    // For mobile path (ScriptProcessor), amplification is applied at sample level,
    // so masterGainNode should stay at 1.0.
    if (this.masterGainNode && !this.isPaused && !this.useScriptProcessor) {
      this.masterGainNode.gain.value = clampedVolume;
    }

    // NOTE: Do NOT update per-chunk playbackGains - they should remain at 1.0 (pass-through)
    // Amplification is handled by masterGainNode (desktop) or sample-level (mobile)

    // Update HTML audio elements (clamped to 0-1 as per HTML spec)
    this.playbackAudioElements.forEach(audioElement => {
      audioElement.volume = Math.min(1.0, clampedVolume);
    });
  }

  /**
   * Clean up all playback resources
   */
  cleanup(): void {
    this.stopPlaybackMonitoring();
    
    const playbackIds = Array.from(this.playbackSources.keys());
    playbackIds.forEach(id => this.stopAudioStream(id));
    
    this.playbackAnalyserNode = null;
    
    // 🎮 Cleanup object pool
    this.nodePool.cleanup();
    this.activePooledNodes.clear();
  }
}

